/**
 * CABPage — Main page for the Common Application Blueprint mode.
 *
 * Split layout:
 * - Left panel: File upload area + file list + action bar
 * - Right panel: Preview (empty state initially, later HTML preview)
 * - Progress sidebar: Vertical step progress (visible during generation)
 *
 * Integrates WebSocket for real-time progress updates.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Text, Icon, Modal } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { Button } from '../components/Button/Button';
import { CABFileUpload } from '../components/CAB/CABFileUpload';
import { CABFileList } from '../components/CAB/CABFileList';
import { CABEmptyState } from '../components/CAB/CABEmptyState';
import { CABProgressSidebar } from '../components/CAB/CABProgressSidebar';
import { CABActionBar } from '../components/CAB/CABActionBar';
import { CABSectionSelector, DEFAULT_ENABLED_SECTIONS } from '../components/CAB/CABSectionSelector';
import { DEFAULT_MODEL_TIER } from '../components/CAB/CABModelSelector';
import { useCABWebSocket } from '../hooks/useCABWebSocket';
import { CABErrorState } from '../components/CAB/CABErrorState';
import { CABCostPanel } from '../components/CAB/CABCostPanel';
import { CABShareModal } from '../components/CAB/CABShareModal';
import { CABPanelTabs, CABChatPanel } from '../components/CAB';
import type { CABPanelTab } from '../components/CAB';
import { IfHasPermission } from '../components/IfHasPermission';
import { FormAlert } from '../components/FormAlert/FormAlert';
import { Permissions } from '../constants';
import type { Message } from '../types/chat';
import type { CABTag } from '../types/cabTags';
import { useAuth } from '../contexts/AuthContext';
import { formatTimestamp } from '../utils/dateUtils';
import {
  uploadCABFile,
  listCABFiles,
  deleteCABFile,
  downloadCABFile,
  getCABStatus,
  getCABProgress,
  getCABHtml,
  generateCAB,
  deleteCAB,
  pauseCAB,
  cancelCAB,
  resumeCAB,
  regenerateCAB,
  downloadCABMarkdown,
  downloadCABPdf,
  updateCABShareSettings,
  getCABVersion,
  getCABHtmlEditable,
  patchCAB,
  listCABVersions,
  getCABVersionHtml,
  restoreCABVersion,
  reviewCABEdits,
  applyCABReview,
  getActiveReviewJob,
  pollReviewJob,
  recordChangeDecision,
  getReviewDecisions,
  getCabChatMessages,
  sendCabChatMessage,
} from '../services/cabApi';
import type { CABVersion } from '../services/cabApi';
import { CABVersionHistory } from '../components/CAB/CABVersionHistory';
import type {
  CABFile,
  CABSessionStatus,
  CABPhasesInfo,
  CABErrorInfo,
  CABCostInfo,
  FileUploadState,
  PatchOp,
  CABIframeMessage,
  EditReviewResponse,
  ReviewChoice,
  CabChatMessage,
} from '../types/cab';
import { PlatformType } from '../types/project';
import { validateCABFilesForUpload } from '../utils/fileUploadConfig';
import { generateTicketsFromCAB, getCabTicketCount, getTranslateJobStatus } from '../services/ticketApi';
import { getCABJson } from '../services/cabApi';


interface CABPageProps {
  onProjectCostUpdate?: () => void;
  permissions?: string[];
  platformType?: PlatformType;
  onGenerateDesign?: () => void;
  onNavigateToTickets?: () => void;
  // Web projects have no platformType but still expose the Generate Tickets
  // button (creates one ticket per user story). Kept separate from
  // platformType, which gates CAB generation/design behaviour.
  enableTicketGeneration?: boolean;
}

// Inline sparkle glyph used in the AI Review badge — small, currentColor-driven
// so it inherits chip text color in both light and dark mode.
const SparkleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    className={className}
  >
    <path d="M12 1.5l1.95 6.55L20.5 10l-6.55 1.95L12 18.5l-1.95-6.55L3.5 10l6.55-1.95L12 1.5z" />
    <path d="M19 14l.78 2.22L22 17l-2.22.78L19 20l-.78-2.22L16 17l2.22-.78L19 14z" opacity="0.85" />
  </svg>
);

/** Convert a CAB dot-path like "project_identity.risks[2].title" into a
 *  human-readable breadcrumb "Project Identity › Risks › #3 › Title". */
function pathToBreadcrumb(path: string): string {
  if (!path) return '—';
  const toTitle = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
  const parts: string[] = [];
  path.split('.').forEach((seg: string) => {
    const m = seg.match(/^(.+?)\[(\d+)\]$/);
    if (m) {
      parts.push(toTitle(m[1]), `#${parseInt(m[2]) + 1}`);
    } else {
      parts.push(toTitle(seg));
    }
  });
  return parts.join(' › ');
}

/** Render any op value as a readable short string for the WAS / NOW row.
 *  Objects are shown as "key: value · key: value" instead of raw JSON. */
function truncateValue(val: unknown, maxLen = 160): string {
  if (val == null) return '—';
  if (typeof val === 'object' && !Array.isArray(val)) {
    try {
      const pairs = Object.entries(val as Record<string, unknown>)
        .map(([k, v]) => {
          const vStr = v == null ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v);
          return `${k}: ${vStr}`;
        })
        .join('  ·  ');
      return pairs.length > maxLen ? pairs.slice(0, maxLen) + '…' : pairs;
    } catch { /* fall through */ }
  }
  if (Array.isArray(val)) {
    const s = (val as unknown[]).map(i => (typeof i === 'object' ? JSON.stringify(i) : String(i))).join(', ');
    return s.length > maxLen ? s.slice(0, maxLen) + '…' : s;
  }
  const s = String(val);
  return s.length > maxLen ? s.slice(0, maxLen) + '…' : s;
}

// ─────────────────────────────────────────────────────────────────────────────
// Path helpers — used by Save All to merge per-leaf accept/reject decisions
// back into a parent op's value. Paths use dot+bracket notation matching the
// renderer's `data-cab-diff-path`, e.g. `userStories[0].acceptanceCriteria[1]`.
// ─────────────────────────────────────────────────────────────────────────────
function deepClone<T>(v: T): T {
  if (v === null || typeof v !== 'object') return v;
  try { return structuredClone(v); } catch { return JSON.parse(JSON.stringify(v)); }
}

// Tokenize "foo.bar[2].baz" → ["foo", "bar", 2, "baz"]
function tokenizePath(path: string): Array<string | number> {
  if (!path) return [];
  const tokens: Array<string | number> = [];
  const re = /([^.[\]]+)|\[(\d+)\]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(path)) !== null) {
    if (m[1] !== undefined) tokens.push(m[1]);
    else if (m[2] !== undefined) tokens.push(parseInt(m[2], 10));
  }
  return tokens;
}

function getByPath(root: any, path: string): any {
  if (!path) return root;
  let cur: any = root;
  for (const tok of tokenizePath(path)) {
    if (cur == null) return undefined;
    cur = cur[tok as any];
  }
  return cur;
}

// Set `value` at `path` in `root` (mutating). Creates objects/arrays as needed
// based on the next token type.
function setByPath(root: any, path: string, value: any): void {
  const tokens = tokenizePath(path);
  if (tokens.length === 0) return;
  let cur: any = root;
  for (let i = 0; i < tokens.length - 1; i++) {
    const tok = tokens[i];
    const nextTok = tokens[i + 1];
    if (cur[tok as any] == null) {
      cur[tok as any] = typeof nextTok === 'number' ? [] : {};
    }
    cur = cur[tok as any];
  }
  cur[tokens[tokens.length - 1] as any] = value;
}

const CABPage: React.FC<CABPageProps> = ({ onProjectCostUpdate, permissions = [], platformType, onGenerateDesign, onNavigateToTickets, enableTicketGeneration }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const { user: currentUser } = useAuth();

  // ────────────────────────────────────────────────────────────
  // STATE
  // ────────────────────────────────────────────────────────────

  const [files, setFiles] = useState<CABFile[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<FileUploadState[]>([]);
  const [sessionStatus, setSessionStatus] = useState<CABSessionStatus>('idle');
  const [phases, setPhases] = useState<CABPhasesInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorInfo, setErrorInfo] = useState<CABErrorInfo | null>(null);
  const [currentProgressStage, setCurrentProgressStage] = useState<string | null>(null);
  const [overallPercentage, setOverallPercentage] = useState(0);
  const [costInfo, setCostInfo] = useState<CABCostInfo | null>(null);
  const [totalDurationSeconds, setTotalDurationSeconds] = useState<number | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isDownloadingMarkdown, setIsDownloadingMarkdown] = useState(false);
  const [shareableLinkEnabled, setShareableLinkEnabled] = useState(false);
  const [cabSessionId, setCabSessionId] = useState<string | undefined>(undefined);
  const [showShareModal, setShowShareModal] = useState(false);
  const [enabledSections, setEnabledSections] = useState<string[]>(DEFAULT_ENABLED_SECTIONS);

  // Guard against stale preview responses: each loadPreview call gets a
  // sequence number. Only the response from the latest call is applied.
  const previewSeqRef = useRef(0);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editableHtml, setEditableHtml] = useState<string | null>(null);
  const [isLoadingEditable, setIsLoadingEditable] = useState(false);
  // Stays true from the moment the user clicks Edit until the editable
  // iframe has fully parsed the HTML, applied edit-mode styles and signalled
  // `cab-iframe-ready`. Without this, the overlay would disappear right
  // after the network fetch resolves and the user would stare at a blank
  // (or stale) iframe for the multi-second parse with no feedback.
  const [isEnteringEditMode, setIsEnteringEditMode] = useState(false);
  // Stays true while we tear down edit mode (Discard) and the iframe is
  // re-rendering the read-only preview HTML, until `iframe.onload` fires.
  const [isExitingEditMode, setIsExitingEditMode] = useState(false);
  const exitTimeoutRef = useRef<number | null>(null);
  const [cabVersion, setCabVersion] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const timerCleanupRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── AI Edit Reviewer state ──
  // Triggered by clicking "Review with AI" while in edit mode. We collect
  // the iframe's manual ops, post them to /review-edits, and switch to a
  // separate "review" view that shows the diff-annotated HTML and the
  // three-button toolbar (Save AI Suggestion / Save Manual Changes / Discard All).
  // The original manual ops are stashed in `pendingManualOps` for potential re-hydration.
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [reviewData, setReviewData] = useState<EditReviewResponse | null>(null);
  const [pendingManualOps, setPendingManualOps] = useState<PatchOp[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [activeReviewJobId, setActiveReviewJobId] = useState<string | null>(null);
  const [isApplyingReview, setIsApplyingReview] = useState(false);
  // When true, the next `cab-iframe-ready` message should re-apply manual
  // ops via `cab-restore-ops` (Back-to-Editing path).
  const restoreOpsOnReadyRef = useRef<PatchOp[] | null>(null);
  // Set to true when entering review mode; consumed by iframe onLoad to auto-jump to the first change.
  const autoJumpOnReviewLoadRef = useRef(false);
  // Set to true when hydrating from DB decisions (page refresh path).
  // Triggers a jump to the first UNDECIDED change once both the diff index
  // and the seeded opDecisions are ready.
  const pendingFirstUndecidedJumpRef = useRef(false);

  // DOM-ordered flat list of diff entries built once when the review iframe loads.
  // Each entry represents ONE clickable change — could be a leaf inside a parent op
  // (e.g. userStories[0].steps[0].action when the parent op is userStories[0]).
  //   - el: the actual <span data-cab-diff-target> in the iframe (highlight target)
  //   - leafPath: the path on that span (what the user actually changed)
  //   - opPath: the path of the underlying op in reviewData (what gets accepted/rejected)
  //   - kind: manual | ai
  //   - oldValue / newValue: text extracted from the span's <del>/<ins> children, so
  //     Row 3 always shows the change actually highlighted (not the parent op's blob).
  type DiffEntryKind =
    | 'manual'
    | 'manual-insert'
    | 'manual-remove'
    | 'ai'
    | 'ai-insert'
    | 'ai-remove';

  const isManualDiffKind = (kind: string) =>
    kind === 'manual' || kind === 'manual-insert' || kind === 'manual-remove';

  const isAiDiffKind = (kind: string) =>
    kind === 'ai' || kind === 'ai-insert' || kind === 'ai-remove';

  const parentCabPath = (path: string): string | null => {
    const cut = Math.max(path.lastIndexOf('.'), path.lastIndexOf('['));
    return cut > 0 ? path.slice(0, cut) : null;
  };

  type DiffEntry = {
    el: Element;
    kind: DiffEntryKind;
    leafPath: string;
    opPath: string;
    oldValue: string;
    newValue: string;
    rationale?: string | null;
  };
  const diffElementsRef = useRef<DiffEntry[]>([]);
  // Total navigable entries (mirrors diffElementsRef.current.length) — kept in state so
  // React re-renders Row 3 once the index has been built after iframe load.
  const [diffTotal, setDiffTotal] = useState(0);

  // Combined flat navigation index across DOM-ordered diff entries. -1 = nothing focused.
  const [combinedIdx, setCombinedIdx] = useState(-1);
  // Active diff entry — drives Row 3 rendering (WAS/NOW from the actual highlighted leaf).
  const [activeEntry, setActiveEntry] = useState<DiffEntry | null>(null);

  // Per-change accept/reject decisions. Key: `${kind}_${path}`, value: 'accepted' | 'rejected'.
  // Absent key means pending (not yet decided).
  const [opDecisions, setOpDecisions] = useState<Record<string, 'accepted' | 'rejected'>>({});

  // Tracks which kind (manual / ai) was navigated most recently so the detail
  // bar always shows the last change the user explicitly jumped to.
  const [activeKind, setActiveKind] = useState<DiffEntryKind | null>(null);

  // Current jump indices for Prev/Next navigation within each diff kind.
  const [manualJumpIdx, setManualJumpIdx] = useState(-1);
  const [aiJumpIdx, setAiJumpIdx] = useState(-1);

  // Version history state
  const [versions, setVersions] = useState<CABVersion[]>([]);
  const [selectedVersionTs, setSelectedVersionTs] = useState<number | null>(null);
  const [versionHtml, setVersionHtml] = useState<string | null>(null);
  const [isLoadingVersionHtml, setIsLoadingVersionHtml] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<number | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const versionHtmlSeqRef = useRef(0);

  // Left panel tab state
  const [activeLeftTab, setActiveLeftTab] = useState<CABPanelTab>('config');

  const [chatMessages, setChatMessages] = useState<Message[]>([]);
  const [isSendingChatMessage, setIsSendingChatMessage] = useState(false);
  const [activeChatJobId, setActiveChatJobId] = useState<string | null>(null);
  const [chatMessagesLoaded, setChatMessagesLoaded] = useState(false);
  const chatPollAbortRef = useRef<AbortController | null>(null);
  const [hasMoreChatMessages, setHasMoreChatMessages] = useState(false);
  const [isLoadingMoreChatMessages, setIsLoadingMoreChatMessages] = useState(false);

  const [cabJson, setCabJson] = useState<any>(null);
  // Ticket generation state
  const [isGeneratingTickets, setIsGeneratingTickets] = useState(false);
  const [ticketGenDone, setTicketGenDone] = useState(false);
  const [showTicketGenConfirm, setShowTicketGenConfirm] = useState(false);
  const [ticketGenMessage, setTicketGenMessage] = useState<string>('');
  const [ticketGenError, setTicketGenError] = useState(false);
  // A job was already running, so this request started nothing. Not an error —
  // it just changes the result card's heading from "Started" to "Already Running".
  const [ticketGenAlreadyRunning, setTicketGenAlreadyRunning] = useState(false);

  // ────────────────────────────────────────────────────────────
  // REVIEW JOB RECOVERY (persist across refresh / tab close)
  // ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    const controller = new AbortController();

    (async () => {
      try {
        const job = await getActiveReviewJob(projectId);
        if (cancelled || !job) return;

        if (job.status === 'completed' && job.result) {
          // If another reviewer already finalised this review, don't re-enter
          // review mode — just show the latest saved preview.
          if (job.applied_by) {
            if (!cancelled) {
              await Promise.all([loadPreview(false), loadVersions(), loadData(false)]);
            }
            return;
          }

          // Only enter review mode if there are actual changes to review
          const hasChangesToReview = job.result.ai_ops.length > 0 || job.result.manual_ops.length > 0;
          
          if (hasChangesToReview) {
            // Hydrate the completed review directly — no re-polling needed.
            setOpDecisions({});
            setCombinedIdx(-1);
            setActiveKind(null);
            setActiveEntry(null);
            setDiffTotal(0);
            diffElementsRef.current = [];
            autoJumpOnReviewLoadRef.current = true;
            setReviewData(job.result);
            setIsReviewMode(true);
            setIsEditMode(false);
            setEditableHtml(null);
            // Seed shared decision state so this reviewer sees decisions recorded
            // by others before this page load.
            try {
              const { decisions } = await getReviewDecisions(projectId, job.result.review_id);
              const seeded: Record<string, 'accepted' | 'rejected'> = {};
              for (const [key, info] of Object.entries(decisions)) {
                seeded[key] = info.decision;
              }
              if (!cancelled) {
                // Set the flag before seeding so the useEffect can detect the
                // refresh path and jump to the first undecided change.
                pendingFirstUndecidedJumpRef.current = true;
                setOpDecisions(seeded);
              }
            } catch {
              // Best-effort — missing decisions just means everything shows as pending
            }
          }
          // If no changes to review (e.g., "no changes needed"), skip review mode
        } else if (job.status === 'queued' || job.status === 'running') {
          // Resume polling for an in-flight job started before the page reload.
          const isChatJob = job.source === 'chat';
          setIsValidating(true);
          setActiveReviewJobId(job.job_id);
          
          // If this is a chat job, also add thinking message to chat UI
          if (isChatJob) {
            setIsSendingChatMessage(true);
            const thinkingMessage: Message = {
              id: `thinking-${Date.now()}`,
              role: 'assistant',
              content: '',
              timestamp: formatTimestamp(new Date(), { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }),
              isThinking: true,
              thinkingMessage: 'Thinking...',
            };
            setChatMessages(prev => {
              if (prev.some(msg => msg.isThinking)) return prev;
              return [...prev, thinkingMessage];
            });
          }
          
          try {
            const result = await pollReviewJob(
              projectId,
              job.job_id,
              job.poll_interval_sec,
              { signal: controller.signal },
            );
            if (cancelled) return;
            
            // For chat jobs, reload messages to get the assistant response (replaces thinking message)
            if (isChatJob) {
              await loadChatMessages();
            }
            
            // Only enter review mode if there are actual changes to review
            const hasChangesToReview = result.ai_ops.length > 0 || result.manual_ops.length > 0;
            
            if (!result.too_broad && hasChangesToReview) {
              setOpDecisions({});
              setCombinedIdx(-1);
              setActiveKind(null);
              setActiveEntry(null);
              setDiffTotal(0);
              diffElementsRef.current = [];
              autoJumpOnReviewLoadRef.current = true;
              setReviewData(result);
              setIsReviewMode(true);
              setIsEditMode(false);
              setEditableHtml(null);
              // Seed shared decision state after poll completes.
              try {
                const { decisions } = await getReviewDecisions(projectId, result.review_id);
                const seeded: Record<string, 'accepted' | 'rejected'> = {};
                for (const [key, info] of Object.entries(decisions)) {
                  seeded[key] = info.decision;
                }
                if (!cancelled) {
                  pendingFirstUndecidedJumpRef.current = true;
                  setOpDecisions(seeded);
                }
              } catch {
                // Best-effort
              }
            }
            // If no changes or too_broad, skip review mode
          } catch (err: any) {
            if (err.name !== 'AbortError' && !cancelled) {
              // Remove thinking message on error for chat jobs
              if (isChatJob) {
                setChatMessages(prev => prev.filter(msg => !msg.isThinking));
              }
              setToastMessage({
                message: 'AI review could not be resumed — please try again.',
                variant: 'error',
              });
            }
          } finally {
            if (!cancelled) {
              setIsValidating(false);
              setActiveReviewJobId(null);
              setIsSendingChatMessage(false);
            }
          }
        }
      } catch {
        // Best-effort: ignore errors on mount check
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ────────────────────────────────────────────────────────────
  // DATA LOADING
  // ────────────────────────────────────────────────────────────

  const loadData = useCallback(async (showLoading = true) => {
    if (!projectId) return;
    try {
      if (showLoading) {
        setIsLoading(true);
      }
      const [filesRes, statusRes] = await Promise.all([
        listCABFiles(projectId),
        getCABStatus(projectId),
      ]);
      setFiles(filesRes.files);
      setSessionStatus(statusRes.status);
      if (statusRes.phases) {
        setPhases(statusRes.phases);
      }
      if (statusRes.error_info) {
        setErrorInfo(statusRes.error_info);
      }
      if (statusRes.cost) {
        setCostInfo(statusRes.cost);
      }
      if (statusRes.total_duration_seconds != null) {
        setTotalDurationSeconds(statusRes.total_duration_seconds);
      }
      // Load enabled_sections from session. model_tier is not loaded back:
      // the picker is hidden and every (re)generation sends DEFAULT_MODEL_TIER.
      if (statusRes.enabled_sections) {
        setEnabledSections(statusRes.enabled_sections);
      }
      setShareableLinkEnabled(statusRes.readonly_sharable_link_enabled || false);
      if (statusRes.id) {
        setCabSessionId(statusRes.id);
      }
    } catch (err: any) {
      console.error('Failed to load CAB data:', err);
      setError(extractErrorMessage(err, 'Failed to load data'));
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  }, [projectId]);

  const loadPreview = useCallback(async (wip = true) => {
    if (!projectId) return;
    const seq = ++previewSeqRef.current;
    try {
      setIsLoadingPreview(true);
      const html = await getCABHtml(projectId, wip);
      // Only apply the result if no newer request has been issued since
      if (seq === previewSeqRef.current) {
        setPreviewHtml(html);
      }
    } catch (err: any) {
      console.warn('Failed to load CAB preview:', err);
      // Don't show error to user — preview is optional during generation
    } finally {
      if (seq === previewSeqRef.current) {
        setIsLoadingPreview(false);
      }
    }
  }, [projectId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const loadCABJson = async () => {
      if (!projectId || sessionStatus !== 'completed') {
        setCabJson(null);
        return;
      }

      try {
        const json = await getCABJson(projectId);
        setCabJson(json);
      } catch (err) {
        console.warn('Failed to load CAB JSON:', err);
        setCabJson(null);
      }
    };

    loadCABJson();
  }, [projectId, sessionStatus]);

  // Auto-load preview when generation is completed (final, non-WIP)
  useEffect(() => {
    if (sessionStatus === 'completed' && projectId && !previewHtml) {
      loadPreview(false);
    }
  }, [sessionStatus, projectId]); // eslint-disable-line react-hooks/exhaustive-deps


  // ────────────────────────────────────────────────────────────
  // TICKET GENERATION
  // ────────────────────────────────────────────────────────────

  const handleGenerateTickets = useCallback(async () => {
    if (!projectId) return;
    setIsGeneratingTickets(true);
    setTicketGenDone(false);
    setTicketGenError(false);
    setTicketGenAlreadyRunning(false);
    try {
      const result = await generateTicketsFromCAB(projectId);
      if (result.job_id) {
        setTicketGenMessage(
          'Veylo Agent is generating tickets from CAB. Switch to the Tickets tab to track progress.'
        );
      } else {
        setTicketGenMessage(result.message || 'No CAB items found to generate tickets from.');
      }
      setTicketGenDone(true);
    } catch (err) {
      console.error('Failed to start ticket translation job:', err);
      // 409 means a job is already running for this project — that is not a
      // failure, so present it as information (with the route to track it)
      // rather than as an error the user should retry.
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        setTicketGenError(false);
        setTicketGenAlreadyRunning(true);
        setTicketGenMessage(
          'Ticket generation is already in progress for this project. Switch to the Tickets tab to track progress.'
        );
      } else {
        setTicketGenError(true);
        setTicketGenMessage(
          'Ticket generation service is currently unavailable. Please try again in a few moments.'
        );
      }
      setTicketGenDone(true);
    } finally {
      setIsGeneratingTickets(false);
    }
  }, [projectId]);

  const handleGenerateTicketsClick = useCallback(async () => {
    if (!projectId) return;

    // Bail out early when a job is already running, so the user is never shown the
    // destructive "this will replace existing tickets" confirmation for a request
    // the backend will reject anyway. The backend 409 remains the authority — this
    // is only here to keep the dialog honest.
    try {
      const inFlight = await getTranslateJobStatus(projectId);
      if (inFlight) {
        setTicketGenError(false);
        setTicketGenAlreadyRunning(true);
        setTicketGenMessage(
          'Ticket generation is already in progress for this project. Switch to the Tickets tab to track progress.'
        );
        setTicketGenDone(true);
        return;
      }
    } catch (err) {
      // Status unavailable — fall through and let the backend guard decide.
      console.warn('Could not check for an in-flight ticket generation job:', err);
    }

    try {
      const count = await getCabTicketCount(projectId);
      if (count > 0) {
        setShowTicketGenConfirm(true);
      } else {
        handleGenerateTickets();
      }
    } catch (err) {
      console.error('Failed to check CAB ticket count:', err);
      handleGenerateTickets();
    }
  }, [projectId, handleGenerateTickets]);

  // ────────────────────────────────────────────────────────────
  // VERSION HISTORY
  // ────────────────────────────────────────────────────────────

  const loadVersions = useCallback(async () => {
    if (!projectId) return;
    try {
      const list = await listCABVersions(projectId);
      setVersions(list);
    } catch (err) {
      console.warn('Failed to load CAB versions:', err);
      setVersions([]);
    }
  }, [projectId]);

  // Load versions whenever CAB becomes available (or status changes back to idle/cancelled)
  useEffect(() => {
    if (!projectId) return;
    if (sessionStatus === 'completed') {
      loadVersions();
    } else {
      setVersions([]);
      setSelectedVersionTs(null);
      setVersionHtml(null);
    }
  }, [sessionStatus, projectId, loadVersions]);

  // Fetch snapshot HTML when a historical version is selected
  useEffect(() => {
    if (!projectId || selectedVersionTs == null) {
      setVersionHtml(null);
      return;
    }
    const seq = ++versionHtmlSeqRef.current;
    setIsLoadingVersionHtml(true);
    getCABVersionHtml(projectId, selectedVersionTs)
      .then((html) => {
        if (seq === versionHtmlSeqRef.current) {
          setVersionHtml(html);
        }
      })
      .catch((err) => {
        console.error('Failed to load CAB version HTML:', err);
        if (seq === versionHtmlSeqRef.current) {
          setToastMessage({ message: 'Failed to load version preview', variant: 'error' });
          setSelectedVersionTs(null);
        }
      })
      .finally(() => {
        if (seq === versionHtmlSeqRef.current) {
          setIsLoadingVersionHtml(false);
        }
      });
  }, [projectId, selectedVersionTs]);

  const handleConfirmRestore = useCallback(async () => {
    if (!projectId || restoreTarget == null) return;
    try {
      setIsRestoring(true);
      await restoreCABVersion(projectId, restoreTarget);
      setRestoreTarget(null);
      setSelectedVersionTs(null);
      setVersionHtml(null);
      await Promise.all([loadVersions(), loadPreview(false)]);
      setToastMessage({ message: 'Successfully restored selected version', variant: 'success' });
    } catch (err: any) {
      console.error('Failed to restore CAB version:', err);
      setToastMessage({
        message: extractErrorMessage(err, 'Failed to restore version'),
        variant: 'error',
      });
    } finally {
      setIsRestoring(false);
    }
  }, [projectId, restoreTarget, loadVersions, loadPreview]);

  // ────────────────────────────────────────────────────────────
  // WEBSOCKET
  // ────────────────────────────────────────────────────────────

  useCABWebSocket({
    projectId,
    enabled: !!projectId,
    onProgressUpdate: (data) => {
      if (data.phases) setPhases(data.phases);
      if (data.current_phase_name) setCurrentProgressStage(data.current_phase_name);
      if (data.overall_progress?.percentage !== undefined) {
        setOverallPercentage(data.overall_progress.percentage);
      }
      if (data.elapsed_seconds != null) {
        setTotalDurationSeconds(data.elapsed_seconds);
      }
    },
    onPhaseCompleted: (phase) => {
      // Refresh files to get updated parse statuses
      if (phase === 'file_processing' && projectId) {
        listCABFiles(projectId).then((res) => setFiles(res.files)).catch(() => {});
      }
    },
    onSkeletonReady: () => {
      // Load WIP preview as soon as skeleton is ready
      if (projectId) {
        loadPreview();
      }
    },
    onSectionReady: (section) => {
      // Reload preview as each section completes
      if (projectId) {
        loadPreview();
      }
    },
    onCompleted: (data) => {
      setSessionStatus('completed');
      setCurrentProgressStage(null);
      if (data?.total_duration_seconds != null) {
        setTotalDurationSeconds(data.total_duration_seconds);
      }
      loadData(false);
      loadPreview(false);
      onProjectCostUpdate?.();
    },
    onError: (data) => {
      setSessionStatus('error');
      setError(data?.message || 'Generation failed');
    },
    onPaused: (data) => {
      setSessionStatus('paused');
      setCurrentProgressStage(null);
      loadData(false);
      onProjectCostUpdate?.();
    },
    onCancelled: (data) => {
      setSessionStatus('cancelled');
      setCurrentProgressStage(null);
      setPhases(null);
      loadData(false);
      onProjectCostUpdate?.();
    },
    onChatUpdated: async () => {
      // Another collaborator's chat AI job just finished — reload messages
      // so this user sees the new assistant response without a page refresh.
      await loadChatMessages();
      // Only open the review panel for users who can apply changes.
      // Read-only users just see the updated chat messages, not the diff.
      if (projectId && !isReviewMode && permissions.includes(Permissions.UpdateProjectPermission)) {
        try {
          const job = await getActiveReviewJob(projectId);
          if (job?.status === 'completed' && job.result) {
            const hasChanges = job.result.ai_ops.length > 0 || job.result.manual_ops.length > 0;
            if (hasChanges) {
              setReviewData(job.result);
              setIsReviewMode(true);
              setIsEditMode(false);
              await loadPreview(false);
            }
          }
        } catch {
          // Non-fatal — review panel will remain closed; user can reload if needed
        }
      }
    },
  });

  // ────────────────────────────────────────────────────────────
  // POLLING FALLBACK — catches up when WebSocket is slow/missed
  // ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (sessionStatus !== 'processing' || !projectId) return;

    let isMounted = true;
    let lastSkeletonStatus: string | undefined;
    let lastSectionGenerationStatus: string | undefined;

    const pollProgress = async () => {
      try {
        const progress = await getCABProgress(projectId);
        if (!isMounted) return;

        if (progress.phases) {
          // Only update phases if data actually changed (avoids flicker)
          setPhases((prev) => {
            const newJson = JSON.stringify(progress.phases);
            const prevJson = prev ? JSON.stringify(prev) : '';
            return newJson !== prevJson ? progress.phases : prev;
          });

          // Check if skeleton just became ready
          const skeletonStatus = progress.phases.skeleton_assemble?.status;
          if (skeletonStatus === 'completed' && lastSkeletonStatus !== 'completed') {
            loadPreview(); // Load WIP preview with skeleton
          }
          lastSkeletonStatus = skeletonStatus;

          // Check if section generation is running — refresh preview periodically
          const sectionGenStatus = progress.phases.section_generation?.status;
          if (sectionGenStatus === 'running' && previewHtml) {
            loadPreview(); // Refresh to pick up newly completed sections
          }
          lastSectionGenerationStatus = sectionGenStatus;
        }
        if (progress.current_phase_name) {
          setCurrentProgressStage((prev) => prev !== progress.current_phase_name ? progress.current_phase_name : prev);
        }
        if (progress.overall_progress?.percentage !== undefined) {
          setOverallPercentage((prev) => prev !== progress.overall_progress.percentage ? progress.overall_progress.percentage : prev);
        }

        // Detect terminal states from the backend
        if (progress.status === 'completed') {
          setSessionStatus('completed');
          setCurrentProgressStage(null);
          loadData(false);
          loadPreview(false); // Final preview (non-WIP)
          onProjectCostUpdate?.();
          return; // stop polling
        }
        if (progress.status === 'error') {
          setSessionStatus('error');
          loadData(false);
          return; // stop polling
        }
        if (progress.status === 'paused' || progress.status === 'cancelled') {
          setSessionStatus(progress.status);
          return; // stop polling
        }
      } catch (err) {
        console.warn('CAB progress poll failed:', err);
      }
    };

    // Poll immediately on mount, then every 30 seconds (WebSocket is the primary channel)
    pollProgress();
    const intervalId = setInterval(pollProgress, 30000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [sessionStatus, projectId, previewHtml]); // eslint-disable-line react-hooks/exhaustive-deps

  // ────────────────────────────────────────────────────────────
  // FILE HANDLERS
  // ────────────────────────────────────────────────────────────

  /** Safely extract a human-readable error string from an Axios error. */
  const extractErrorMessage = (err: any, fallback: string): string => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    // FastAPI 422 returns detail as an array of validation-error objects
    if (Array.isArray(detail)) {
      const msgs = detail.map((d: any) => d?.msg).filter(Boolean);
      return msgs.length > 0 ? msgs.join('; ') : fallback;
    }
    return fallback;
  };

  // Returns true when the apply-review call failed because another reviewer
  // already finalised the same review. Used to gracefully exit review mode
  // instead of leaving the user stuck on the review screen.
  const isAlreadySavedError = (err: any): boolean => {
    const msg = extractErrorMessage(err, '').toLowerCase();
    return msg.includes('already been saved') || msg.includes('already saved');
  };

  // Shared helper: exit review mode without saving and load the fresh preview.
  // Called when another reviewer has already applied the review.
  const exitReviewDueToAlreadySaved = useCallback(async () => {
    setIsReviewMode(false);
    setIsEditMode(false);
    setReviewData(null);
    setPendingManualOps([]);
    setEditableHtml(null);
    setCabVersion(null);
    setOpDecisions({});
    setCombinedIdx(-1);
    setActiveKind(null);
    setActiveEntry(null);
    setDiffTotal(0);
    diffElementsRef.current = [];
    restoreOpsOnReadyRef.current = null;
    pendingFirstUndecidedJumpRef.current = false;
    await Promise.all([loadPreview(false), loadVersions(), loadData(false)]);
    setToastMessage({
      message: 'This review has already been saved by another reviewer. Showing the latest version.',
      variant: 'error',
    });
  }, [loadPreview, loadVersions, loadData]);

  const handleFilesSelected = useCallback(async (files: File[]) => {
    if (!projectId) return;

    // ── Pre-flight: validate extension + filename length before any network call ──
    const validationErrors = validateCABFilesForUpload(files);
    if (validationErrors.length > 0) {
      setError(validationErrors.join('\n'));
      return;
    }

    setIsUploading(true);
    setError(null);

    // Create initial upload states for all files with AbortControllers
    const initialStates: FileUploadState[] = files.map((file, idx) => ({
      tempId: `temp-${Date.now()}-${idx}`,
      file,
      status: 'pending',
      progress: 0,
      abortController: new AbortController(),
    }));

    // Immediately show files in UI with "pending" status
    setUploadingFiles(initialStates);

    // Upload all files in parallel
    const uploadPromises = initialStates.map(async (uploadState) => {
      try {
        // Update status to "uploading"
        setUploadingFiles((prev) =>
          prev.map((s) =>
            s.tempId === uploadState.tempId
              ? { ...s, status: 'uploading' as const }
              : s
          )
        );

        const result = await uploadCABFile(
          projectId,
          uploadState.file,
          (percent) => {
            // Update individual file progress
            setUploadingFiles((prev) =>
              prev.map((s) =>
                s.tempId === uploadState.tempId
                  ? { ...s, progress: percent }
                  : s
              )
            );
          },
          uploadState.abortController
        );

        // Mark as completed
        setUploadingFiles((prev) =>
          prev.map((s) =>
            s.tempId === uploadState.tempId
              ? {
                  ...s,
                  status: 'completed' as const,
                  progress: 100,
                  fileId: result.file_id,
                }
              : s
          )
        );

        return { success: true, result };
      } catch (err: any) {
        // Check if error is due to abort
        if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
          console.log('File upload cancelled:', uploadState.file.name);
          return { success: false, cancelled: true };
        }

        console.error('File upload failed:', err);
        const errorMsg = extractErrorMessage(err, 'Upload failed');

        // Mark as error
        setUploadingFiles((prev) =>
          prev.map((s) =>
            s.tempId === uploadState.tempId
              ? { ...s, status: 'error' as const, error: errorMsg }
              : s
          )
        );

        return { success: false, error: err };
      }
    });

    // Wait for all uploads to complete (or fail)
    const results = await Promise.allSettled(uploadPromises);

    // Calculate overall progress
    const successCount = results.filter(
      (r) => r.status === 'fulfilled' && r.value.success
    ).length;
    const cancelledCount = results.filter(
      (r) => r.status === 'fulfilled' && r.value.cancelled
    ).length;
    const failCount = results.length - successCount - cancelledCount;

    // Show error if any uploads failed (but not if they were cancelled)
    if (failCount > 0) {
      const failedDetails = results
        .map((r) => (r.status === 'fulfilled' && !r.value.success && !r.value.cancelled ? r.value : null))
        .filter(Boolean)
        .map((r: any) => extractErrorMessage(r.error, 'Upload failed'));
      const uniqueReasons = [...new Set(failedDetails)];
      setError(`${failCount} file(s) failed to upload. ${uniqueReasons.join(' | ')}`);
    }

    // Refresh file list from server, then clear uploading state so
    // there's no moment where both lists show the same file.
    await loadData(false);

    // Server list is now in `files[]` — safe to clear uploading entries.
    // Keep error entries briefly so user can see what failed.
    setUploadingFiles((prev) =>
      prev.filter((s) => s.status === 'error')
    );
    setIsUploading(false);

    if (failCount > 0) {
      setTimeout(() => {
        setUploadingFiles([]);
      }, 3000);
    }
  }, [projectId, loadData, extractErrorMessage]);

  const handleDeleteFile = useCallback(async (fileId: string) => {
    if (!projectId) return;
    try {
      setError(null);
      await deleteCABFile(projectId, fileId);
      setFiles((prev) => prev.filter((f) => f.file_id !== fileId));
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to delete file'));
    }
  }, [projectId]);

  const handleDownloadFile = useCallback(async (fileId: string, fileName: string) => {
    if (!projectId) return;
    try {
      setError(null);
      await downloadCABFile(projectId, fileId, fileName);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to download file'));
    }
  }, [projectId]);

  const handleDeleteAll = useCallback(async () => {
    if (!projectId) return;
    try {
      setError(null);

      // STEP 1: Cancel all in-flight uploads atomically
      uploadingFiles.forEach((uploadState) => {
        if (uploadState.abortController && uploadState.status === 'uploading') {
          uploadState.abortController.abort();
        }
      });

      // STEP 2: Clear upload state immediately (UX feedback)
      setUploadingFiles([]);
      setIsUploading(false);

      // STEP 3: Small delay to ensure abort signals propagate through network stack
      await new Promise(resolve => setTimeout(resolve, 100));

      // STEP 4: Now safely delete everything from backend
      await deleteCAB(projectId);

      // STEP 5: Clear remaining state
      setFiles([]);
      setSessionStatus('idle');
      setPhases(null);
      setPreviewHtml(null);
      setCostInfo(null);
      setTotalDurationSeconds(null);
      setEnabledSections(DEFAULT_ENABLED_SECTIONS);
      onProjectCostUpdate?.();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to delete'));
    }
  }, [projectId, uploadingFiles, onProjectCostUpdate]);

  // ────────────────────────────────────────────────────────────
  // GENERATION HANDLERS
  // ────────────────────────────────────────────────────────────

  const handleGenerate = useCallback(async () => {
    if (!projectId) return;
    if (enabledSections.length === 0) {
      setError('Select at least one section to generate');
      return;
    }
    try {
      setError(null);
      await generateCAB(projectId, { enabled_sections: enabledSections, model_tier: DEFAULT_MODEL_TIER });
      setSessionStatus('processing');
      setPhases(null);
      setOverallPercentage(0);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to start generation'));
    }
  }, [projectId, enabledSections]);

  const handleRegenerate = useCallback(async () => {
    if (!projectId) return;
    try {
      setError(null);
      setErrorInfo(null);
      setSessionStatus('idle');
      setPhases(null);
      setPreviewHtml(null);
      setTotalDurationSeconds(null);
      await regenerateCAB(projectId, { enabled_sections: enabledSections, model_tier: DEFAULT_MODEL_TIER });
      setSessionStatus('processing');
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to regenerate'));
    }
  }, [projectId, enabledSections]);

  const handlePause = useCallback(async () => {
    if (!projectId) return;
    try {
      await pauseCAB(projectId);
      setSessionStatus('paused');
      onProjectCostUpdate?.();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to pause'));
    }
  }, [projectId, onProjectCostUpdate]);

  const handleStop = useCallback(async () => {
    if (!projectId) return;
    try {
      await cancelCAB(projectId);
      setSessionStatus('cancelled');
      setPhases(null);
      setPreviewHtml(null);
      setOverallPercentage(0);
      setCurrentProgressStage(null);
      setTotalDurationSeconds(null);
      onProjectCostUpdate?.();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to stop'));
    }
  }, [projectId, onProjectCostUpdate]);

  const handleResume = useCallback(async () => {
    if (!projectId) return;
    try {
      setError(null);
      setErrorInfo(null);
      await resumeCAB(projectId);
      setSessionStatus('processing');
      onProjectCostUpdate?.();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to resume'));
    }
  }, [projectId, onProjectCostUpdate]);

  // ────────────────────────────────────────────────────────────
  // EDIT MODE HANDLERS
  // ────────────────────────────────────────────────────────────

  const enterEditMode = useCallback(async () => {
    if (!projectId) return;
    try {
      // `isEnteringEditMode` covers the entire perceived operation:
      // network fetch + iframe HTML parse + edit-script init. We only flip
      // it off when the iframe sends `cab-iframe-ready` (see message
      // handler below). `isLoadingEditable` covers just the network leg
      // and is used by the Edit button's spinner.
      setIsLoadingEditable(true);
      setIsEnteringEditMode(true);
      setEditError(null);

      const [html, versionRes] = await Promise.all([
        getCABHtmlEditable(projectId),
        getCABVersion(projectId),
      ]);

      // Toggle the toolbar to Save/Discard first and let the browser paint that
      // frame cleanly. Then, on the next animation frame, swap the iframe's
      // srcdoc — this triggers a multi-second parse of the editable HTML that
      // would otherwise block the parent's first paint and leave the new
      // buttons looking unstyled until the iframe settled.
      setCabVersion(versionRes.version);
      setIsEditMode(true);
      setIsLoadingEditable(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setEditableHtml(html);
        });
      });
    } catch (err: any) {
      console.error('Failed to enter edit mode:', err);
      setEditError(extractErrorMessage(err, 'Failed to load editable version'));
      setIsLoadingEditable(false);
      setIsEnteringEditMode(false);
    }
  }, [projectId]);

  const exitEditMode = useCallback(() => {
    // The iframe has to re-parse the (large) read-only preview HTML when
    // we drop the editable HTML — that re-parse can take a noticeable
    // moment on big CABs and looks like a freeze without feedback.
    // We show the overlay until iframe.onload fires (handled inline) or
    // a 4s safety timeout, whichever comes first.
    setIsExitingEditMode(true);
    setIsEditMode(false);
    setEditableHtml(null);
    setCabVersion(null);
    setEditError(null);
    // Ensure no review-mode artifacts linger when we go back to read-only.
    setIsReviewMode(false);
    setReviewData(null);
    setPendingManualOps([]);
    restoreOpsOnReadyRef.current = null;
    if (exitTimeoutRef.current) {
      window.clearTimeout(exitTimeoutRef.current);
    }
    exitTimeoutRef.current = window.setTimeout(() => {
      setIsExitingEditMode(false);
      exitTimeoutRef.current = null;
    }, 4000);
  }, []);

  useEffect(() => {
    return () => {
      if (exitTimeoutRef.current) {
        window.clearTimeout(exitTimeoutRef.current);
      }
    };
  }, []);

  const handleIframeSave = useCallback(async (ops: PatchOp[]) => {
    if (!projectId || !cabVersion) return;

    // Smooth save flow:
    //   1. isSaving stays true through PATCH + preview reload (single perceived
    //      operation from the user's POV).
    //   2. We only tear down editableHtml AFTER the new previewHtml is in state,
    //      so the iframe never flashes stale or empty content between phases.
    //   3. The in-pane overlay shows "Saving..." then "Loading latest..." over
    //      the iframe throughout — see displayHtml + overlay logic below.
    try {
      setIsSaving(true);
      setEditError(null);
      const result = await patchCAB(projectId, { baseVersion: cabVersion, ops });

      if (!result.success) {
        const errorMsg = result.errors?.length > 0 ? result.errors.join(' | ') : 'Failed to save changes';
        setToastMessage({ message: errorMsg, variant: 'error' });
        return;
      }

      // PATCH succeeded — fetch fresh preview + versions BEFORE leaving edit mode
      // so the user sees a continuous loading state instead of a flash of old content.
      await Promise.all([loadPreview(false), loadVersions()]);

      // Now swap out of edit mode in a single paint to avoid layout flicker.
      // Mark the iframe re-parse window so the overlay stays visible until
      // the read-only iframe's onLoad fires (or 4s safety timeout).
      setIsExitingEditMode(true);
      if (exitTimeoutRef.current) {
        window.clearTimeout(exitTimeoutRef.current);
      }
      exitTimeoutRef.current = window.setTimeout(() => {
        setIsExitingEditMode(false);
        exitTimeoutRef.current = null;
      }, 4000);
      setIsEditMode(false);
      setEditableHtml(null);
      setCabVersion(null);
      setToastMessage({ message: 'Changes saved successfully', variant: 'success' });
    } catch (err: any) {
      console.error('Failed to save CAB edits:', err);
      const errMsg = extractErrorMessage(err, 'Failed to save changes');
      setToastMessage({ message: errMsg, variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [projectId, cabVersion, loadPreview, loadVersions]);

  // ── AI Edit Reviewer handlers ──

  const handleReviewMessage = useCallback(async (ops: PatchOp[]) => {
    // Triggered when the iframe responds to `cab-request-review`. We always
    // make the API call — even with zero ops — so the user gets a clear
    // "no edits to validate" message instead of a silent click.
    if (!projectId || !cabVersion) return;
    if (ops.length === 0) {
      setToastMessage({
        message: 'No edits to review yet — type changes first, then click Review with AI.',
        variant: 'error',
      });
      return;
    }
    try {
      setIsValidating(true);
      setEditError(null);
      const { result: review, jobId } = await reviewCABEdits(projectId, {
        base_version: cabVersion,
        ops,
      });
      setActiveReviewJobId(jobId);
      // Stash the manual ops so Back-to-Editing can restore them.
      setPendingManualOps(ops);
      setReviewData(review);

      // Refresh session status so the costing dashboard reflects the review cost
      loadData(false);
      onProjectCostUpdate?.();

      if (review.too_broad) {
        // Stay in edit mode but tell the user we couldn't run propagation.
        setToastMessage({
          message: review.too_broad_message || 'Too many candidates — narrow the edit and try again.',
          variant: 'error',
        });
      } else {
        // Switch into the review screen. The diff_html replaces the
        // editable iframe srcDoc; the iframe re-parses fresh.
        setOpDecisions({});
        setCombinedIdx(-1);
        setActiveKind(null);
        setActiveEntry(null);
        setDiffTotal(0);
        diffElementsRef.current = [];
        autoJumpOnReviewLoadRef.current = true;
        setIsReviewMode(true);
        setIsEditMode(false);
        setEditableHtml(null);
      }

      // Some manual edits failed to apply before the AI review ran, so the
      // reviewed document is missing them. Warn the user (takes precedence over
      // the too_broad toast above — this is the more critical signal).
      const applyErrors = review.manual_apply_errors ?? [];
      if (applyErrors.length > 0) {
        setToastMessage({
          message: `${applyErrors.length} of your edit(s) could not be applied and were not reviewed. Please re-check and re-apply them.`,
          variant: 'error',
        });
      }
    } catch (err: any) {
      if ((err as DOMException).name === 'AbortError') return;
      console.error('AI review failed:', err);
      setToastMessage({
        message: extractErrorMessage(err, 'AI validation failed — try again or save manual edits.'),
        variant: 'error',
      });
    } finally {
      setIsValidating(false);
    }
  }, [projectId, cabVersion, loadData, onProjectCostUpdate]);

  // Navigate to the nth diff element of the given kind in the review iframe.
  // The iframe DOM is manipulated directly since the diff HTML is static during review.
  // Inject decision CSS into the iframe once. Needed for reviews generated before this
  // CSS was baked into the renderer (older cached reviews lack the style block).
  const ensureDecisionCss = useCallback((doc: Document) => {
    if (doc.getElementById('cab-decision-css')) return;
    const style = doc.createElement('style');
    style.id = 'cab-decision-css';
    style.textContent = [
      // set ops — accepted
      '[data-cab-diff-target].cab-diff-accepted del{display:none}',
      '[data-cab-diff-target].cab-diff-accepted ins{background-color:transparent!important;text-decoration:none!important;color:inherit!important;outline:1.5px solid rgba(22,163,74,.55);outline-offset:1px;border-radius:2px}',
      '[data-cab-diff-target].cab-diff-accepted ins *{background-color:transparent!important;color:inherit!important}',
      // set ops — rejected
      '[data-cab-diff-target].cab-diff-rejected ins{display:none}',
      '[data-cab-diff-target].cab-diff-rejected del{text-decoration:none!important;color:inherit!important;background-color:transparent!important;opacity:.6;outline:1.5px solid rgba(220,38,38,.45);outline-offset:1px;border-radius:2px}',
      '[data-cab-diff-target].cab-diff-rejected del *{text-decoration:none!important;color:inherit!important;background-color:transparent!important}',
      // insert — accepted: remove green tint, show as settled
      '[data-cab-diff-target="ai-insert"].cab-diff-accepted{border-left-color:rgba(22,163,74,.4)!important;background-color:transparent!important}',
      '[data-cab-diff-target="ai-insert"].cab-diff-accepted ins{background-color:transparent!important;outline:1.5px solid rgba(22,163,74,.55);outline-offset:1px;border-radius:2px}',
      // insert — rejected: collapse the new card entirely
      '[data-cab-diff-target="ai-insert"].cab-diff-rejected{display:none!important}',
      // remove — accepted: collapse the deleted card entirely
      '[data-cab-diff-target="ai-remove"].cab-diff-accepted{display:none!important}',
      // remove — rejected: undo strikethrough, restore normal appearance
      '[data-cab-diff-target="ai-remove"].cab-diff-rejected{border-left-color:rgba(156,163,175,.3)!important;background-color:transparent!important;opacity:1!important}',
      '[data-cab-diff-target="ai-remove"].cab-diff-rejected *{text-decoration:none!important;color:inherit!important}',
    ].join('\n');
    doc.head?.appendChild(style);
  }, []);

  // Build the DOM-ordered flat list of diff entries from the review iframe.
  //
  // KEY INSIGHT: The renderer's `expand_leaf_diff_annotations` may explode one
  // top-level op (e.g. `userStories[0]` replacing the whole story dict) into
  // many leaf spans (`userStories[0].title`, `userStories[0].steps[0].action`,
  // …) — each rendered as its own `<span data-cab-diff-target>`. We treat
  // EACH leaf as its own navigable change so prev/next and click highlight the
  // exact element the user sees, not just the first leaf of the parent op.
  //
  // To accept/reject a leaf, we still operate on the parent op (since that's
  // what `reviewData` carries). Multiple leaves of the same parent op share
  // a decision — accepting one accepts the whole op.
  const buildDiffIndex = useCallback((doc: Document) => {
    if (!reviewData) return;

    const manualPaths = new Set(reviewData.manual_ops.map(o => o.path));
    const aiPaths     = new Set(reviewData.ai_ops.map(o => o.path));
    const aiByPath    = new Map(reviewData.ai_ops.map(o => [o.path, o]));

    // Walk a leaf path upward until it matches a known op path. Returns the
    // op path (or the leaf itself if it's already a top-level op).
    const resolveOpPath = (kind: DiffEntryKind, leafPath: string): string | null => {
      const known = isManualDiffKind(kind) ? manualPaths : aiPaths;
      let p = leafPath;
      while (p) {
        if (known.has(p)) return p;
        const parent = parentCabPath(p);
        if (!parent || parent === p) break;
        p = parent;
      }
      return null;
    };

    const entries: DiffEntry[] = [];
    const allEls = doc.querySelectorAll('[data-cab-diff-target]');
    for (const el of Array.from(allEls)) {
      const kind = el.getAttribute('data-cab-diff-target') as DiffEntryKind;
      if (!isManualDiffKind(kind) && !isAiDiffKind(kind)) continue;
      const leafPath = el.getAttribute('data-cab-diff-path') || '';
      const opPath = resolveOpPath(kind, leafPath);
      if (!opPath) continue;
      // Read leaf-level WAS/NOW directly from the DOM children.
      const delEl = el.querySelector(':scope > del');
      const insEl = el.querySelector(':scope > ins');
      const oldValue = delEl?.textContent ?? '';
      const newValue = insEl?.textContent ?? '';
      const aiOp = isAiDiffKind(kind) ? aiByPath.get(opPath) : undefined;
      entries.push({
        el,
        kind,
        leafPath: leafPath || opPath,
        opPath,
        oldValue,
        newValue,
        rationale: aiOp?.rationale ?? null,
      });
    }

    diffElementsRef.current = entries;
    setDiffTotal(entries.length);
  }, [reviewData]);

  // Activate a diff entry by its position in the DOM-ordered list.
  const activateDiffAt = useCallback((newCombined: number) => {
    const entries = diffElementsRef.current;
    if (entries.length === 0 || newCombined < 0 || newCombined >= entries.length) return;

    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;

    doc.querySelectorAll('.cab-diff-active').forEach(el => el.classList.remove('cab-diff-active'));

    const entry = entries[newCombined];
    entry.el.classList.add('cab-diff-active');
    entry.el.scrollIntoView({ behavior: 'smooth', block: 'center' });

    setActiveKind(entry.kind);
    setCombinedIdx(newCombined);
    setActiveEntry(entry);
  }, []);

  // Build (or rebuild) the DOM diff index from the review iframe document.
  const syncReviewDiffIndex = useCallback(
    (options?: { autoJump?: boolean }) => {
      if (!isReviewMode || !reviewData) return 0;
      const doc = iframeRef.current?.contentDocument;
      if (!doc) return 0;
      buildDiffIndex(doc);
      const count = diffElementsRef.current.length;
      if (options?.autoJump && count > 0) {
        activateDiffAt(0);
      }
      return count;
    },
    [isReviewMode, reviewData, buildDiffIndex, activateDiffAt],
  );

  // Combined prev/next: walks DOM-ordered diff entries.
  const jumpToAny = useCallback((direction: 1 | -1) => {
    let total = diffElementsRef.current.length;
    if (total === 0) {
      syncReviewDiffIndex();
      total = diffElementsRef.current.length;
    }
    if (total === 0) return;
    const newCombined = combinedIdx < 0
      ? (direction > 0 ? 0 : total - 1)
      : (combinedIdx + direction + total) % total;
    activateDiffAt(newCombined);
  }, [combinedIdx, activateDiffAt, syncReviewDiffIndex]);

  // Click handler: find the clicked diff element in the DOM-ordered list (by
  // identity, then by leaf path) and activate it.
  const jumpToElement = useCallback((targetEl: Element) => {
    const entries = diffElementsRef.current;
    const idx = entries.findIndex(e => e.el === targetEl);
    if (idx < 0) return;
    activateDiffAt(idx);
  }, [activateDiffAt]);

  // Mark the focused LEAF as accepted or rejected and update the preview.
  // Clicking the same decision again toggles back to pending (fully reversible).
  // Decisions are keyed per leaf so each visible diff can be independently accepted
  // or discarded — even when multiple leaves share the same parent op.
  //
  // The local optimistic update happens first; the API call is fire-and-forget
  // (last-write-wins on the server, so there's no conflict to revert).
  const handleDecide = useCallback((entry: DiffEntry, decision: 'accepted' | 'rejected') => {
    const doc = iframeRef.current?.contentDocument;
    const key = `${entry.kind}_${entry.leafPath}`;
    const isUndo = opDecisions[key] === decision;

    let newDecision: 'accepted' | 'rejected' | null = decision;

    setOpDecisions(prev => {
      if (prev[key] === decision) {
        // Toggle off → pending (clear)
        newDecision = null;
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: decision };
    });

    if (doc) {
      ensureDecisionCss(doc);
      const isToggle = entry.el.classList.contains(`cab-diff-${decision}`);
      entry.el.classList.remove('cab-diff-accepted', 'cab-diff-rejected');
      if (!isToggle) entry.el.classList.add(`cab-diff-${decision}`);
    }

    // Persist to the shared decision store so other reviewers see this
    // decision after their next page refresh.
    if (projectId && reviewData?.review_id) {
      recordChangeDecision(projectId, reviewData.review_id, key, newDecision).catch(
        (err) => console.warn('[CAB] Decision sync failed:', err),
      );
    }
    // UX: after marking a change as accepted, advance focus to the next diff.
    if (decision === 'accepted' && !isUndo) {
      jumpToAny(1);
    }
  }, [ensureDecisionCss, projectId, reviewData, opDecisions, jumpToAny]);

  // Apply all non-rejected ops (accepted + pending both count as "keep").
  // If everything is rejected, treat as discard.
  const handleSaveAll = useCallback(async () => {
    if (!projectId || !reviewData) return;
    try {
      setIsApplyingReview(true);

      // ── Leaf-aware op construction ──────────────────────────────────────
      // Each parent op may have been expanded into multiple leaf diffs in the
      // DOM. Decisions are recorded per leaf, so we reconstruct the final
      // value for each parent op from its kept leaves.
      //
      // Semantics:
      //   • Pending and Accepted leaves → keep (use new value from op.value)
      //   • Rejected leaves            → drop (use old value from op.old_value)
      //   • All leaves rejected        → skip the op entirely
      //   • All leaves kept            → send op as-is (preserves shape)

      const buildOpsForKind = (
        ops: typeof reviewData.manual_ops,
        kind: 'manual' | 'ai',
      ): PatchOp[] => {
        const result: PatchOp[] = [];

        // Group leaves by their parent op path.
        // For 'ai', also include ai-insert / ai-remove entries.
        const matchesKind = (entryKind: DiffEntryKind) =>
          entryKind === kind ||
          (kind === 'ai' && (entryKind === 'ai-insert' || entryKind === 'ai-remove'));

        const leavesByOp = new Map<string, DiffEntry[]>();
        for (const entry of diffElementsRef.current) {
          if (!matchesKind(entry.kind)) continue;
          if (!leavesByOp.has(entry.opPath)) leavesByOp.set(entry.opPath, []);
          leavesByOp.get(entry.opPath)!.push(entry);
        }

        for (const op of ops) {
          const leaves = leavesByOp.get(op.path) ?? [];

          // Decision key helper — mirrors the key format used in handleDecide:
          //   `${entry.kind}_${entry.leafPath}`
          // For ai-insert / ai-remove entries, entry.kind is 'ai-insert' / 'ai-remove',
          // NOT the parent 'ai'. Using the leaf's actual kind prevents key mismatches
          // that caused rejected INSERT/REMOVE ops to slip through as "pending → keep".
          const decisionKey = (leafKind: string, leafPath: string) =>
            `${leafKind}_${leafPath}`;

          // Case 1 — no leaf entries (op had no visible diff, e.g. structural).
          // Try all possible kind prefixes for this op so seeded decisions are honoured.
          if (leaves.length === 0) {
            const kindVariants = kind === 'ai'
              ? ['ai', 'ai-insert', 'ai-remove']
              : [kind];
            const opDecision = kindVariants
              .map(k => opDecisions[`${k}_${op.path}`])
              .find(d => d !== undefined);
            if (opDecision !== 'rejected') {
              result.push({ op: op.op, path: op.path, value: op.value });
            }
            continue;
          }

          // Case 2 — single leaf at the op path itself (no expansion).
          if (leaves.length === 1 && leaves[0].leafPath === op.path) {
            const leaf = leaves[0];
            const d = opDecisions[decisionKey(leaf.kind, leaf.leafPath)];
            if (d !== 'rejected') {
              result.push({ op: op.op, path: op.path, value: op.value });
            }
            continue;
          }

          // Case 3 — multiple leaves. Decide per leaf, reconstruct value.
          const allRejected = leaves.every(
            l => opDecisions[decisionKey(l.kind, l.leafPath)] === 'rejected'
          );
          if (allRejected) continue;

          const allKept = leaves.every(
            l => opDecisions[decisionKey(l.kind, l.leafPath)] !== 'rejected'
          );
          if (allKept) {
            result.push({ op: op.op, path: op.path, value: op.value });
            continue;
          }

          // Mixed: start from op.value (all-new), then revert each rejected
          // leaf back to its old value. We do this on a deep clone so other
          // ops sharing references aren't mutated.
          const merged = deepClone(op.value);
          for (const leaf of leaves) {
            if (opDecisions[decisionKey(leaf.kind, leaf.leafPath)] !== 'rejected') continue;
            // Compute relative path from op.path → leaf.leafPath
            const relPath = leaf.leafPath === op.path
              ? ''
              : leaf.leafPath.startsWith(op.path)
                ? leaf.leafPath.slice(op.path.length).replace(/^\./, '')
                : leaf.leafPath;
            const oldLeafVal = getByPath(op.old_value, relPath);
            setByPath(merged, relPath, oldLeafVal);
          }
          result.push({ op: op.op, path: op.path, value: merged });
        }

        return result;
      };

      const customOps: PatchOp[] = [
        ...buildOpsForKind(reviewData.manual_ops, 'manual'),
        ...buildOpsForKind(reviewData.ai_ops, 'ai'),
      ];

      const result = await applyCABReview(projectId, {
        review_id: reviewData.review_id,
        choice: customOps.length > 0 ? 'save_custom' : 'discard',
        custom_ops: customOps.length > 0 ? customOps : undefined,
      });

      if (!result.success) {
        const errMsg = result.errors?.length ? result.errors.join(' | ') : 'Apply failed';
        setToastMessage({ message: errMsg, variant: 'error' });
        return;
      }

      await Promise.all([loadPreview(false), loadVersions(), loadData(false)]);
      setIsExitingEditMode(true);
      if (exitTimeoutRef.current) window.clearTimeout(exitTimeoutRef.current);
      exitTimeoutRef.current = window.setTimeout(() => {
        setIsExitingEditMode(false);
        exitTimeoutRef.current = null;
      }, 4000);
      setIsReviewMode(false);
      setIsEditMode(false);
      setReviewData(null);
      setPendingManualOps([]);
      setEditableHtml(null);
      setCabVersion(null);
      setOpDecisions({});
      setCombinedIdx(-1);
      setActiveKind(null);
      setActiveEntry(null);
      setDiffTotal(0);
      diffElementsRef.current = [];
      restoreOpsOnReadyRef.current = null;
      setToastMessage({ message: 'Changes saved', variant: 'success' });
    } catch (err: any) {
      console.error('Save all failed:', err);
      if (isAlreadySavedError(err)) {
        await exitReviewDueToAlreadySaved();
      } else {
        setToastMessage({ message: extractErrorMessage(err, 'Failed to save changes'), variant: 'error' });
      }
    } finally {
      setIsApplyingReview(false);
    }
  }, [projectId, reviewData, opDecisions, loadPreview, loadVersions, loadData, isAlreadySavedError, exitReviewDueToAlreadySaved]);

  const handleApplyReview = useCallback(async (choice: ReviewChoice) => {
    if (!projectId || !reviewData) return;
    try {
      setIsApplyingReview(true);
      const result = await applyCABReview(projectId, {
        review_id: reviewData.review_id,
        choice,
      });

      if (!result.success) {
        const errMsg = result.errors?.length ? result.errors.join(' | ') : 'Apply failed';
        setToastMessage({ message: errMsg, variant: 'error' });
        return;
      }

      // All three terminal choices reload the read-only preview and exit
      // both review + edit mode.
      await Promise.all([loadPreview(false), loadVersions(), loadData(false)]);
      setIsExitingEditMode(true);
      if (exitTimeoutRef.current) {
        window.clearTimeout(exitTimeoutRef.current);
      }
      exitTimeoutRef.current = window.setTimeout(() => {
        setIsExitingEditMode(false);
        exitTimeoutRef.current = null;
      }, 4000);
      setIsReviewMode(false);
      setIsEditMode(false);
      setReviewData(null);
      setPendingManualOps([]);
      setEditableHtml(null);
      setCabVersion(null);
      setOpDecisions({});
      setCombinedIdx(-1);
      setActiveKind(null);
      setActiveEntry(null);
      setDiffTotal(0);
      diffElementsRef.current = [];
      restoreOpsOnReadyRef.current = null;

      const labels: Record<string, string> = {
        save_ai: 'AI suggestions saved',
        save_manual_only: 'Manual edits saved',
        discard: 'All changes discarded',
      };
      setToastMessage({ message: labels[choice] ?? 'Changes applied', variant: 'success' });
    } catch (err: any) {
      console.error('Apply review failed:', err);
      if (isAlreadySavedError(err)) {
        await exitReviewDueToAlreadySaved();
      } else {
        setToastMessage({
          message: extractErrorMessage(err, 'Failed to apply review'),
          variant: 'error',
        });
      }
    } finally {
      setIsApplyingReview(false);
    }
  }, [projectId, reviewData, loadPreview, loadVersions, loadData, isAlreadySavedError, exitReviewDueToAlreadySaved]);

  const handleBackToEditing = useCallback(async () => {
    // Returns to edit mode with the user's manual edits restored. The
    // editable iframe re-renders fresh; once `cab-iframe-ready` fires, we
    // post `cab-restore-ops` with the stashed manual ops.
    if (!projectId || pendingManualOps.length === 0) {
      setIsReviewMode(false);
      setReviewData(null);
      return;
    }
    try {
      setIsLoadingEditable(true);
      setIsEnteringEditMode(true);
      setEditError(null);
      const [html, versionRes] = await Promise.all([
        getCABHtmlEditable(projectId),
        getCABVersion(projectId),
      ]);
      restoreOpsOnReadyRef.current = pendingManualOps;
      setReviewData(null);
      setIsReviewMode(false);
      setCabVersion(versionRes.version);
      setIsEditMode(true);
      setIsLoadingEditable(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setEditableHtml(html);
        });
      });
    } catch (err: any) {
      console.error('Back-to-editing failed:', err);
      setEditError(extractErrorMessage(err, 'Failed to return to editor'));
      setIsLoadingEditable(false);
      setIsEnteringEditMode(false);
    }
  }, [projectId, pendingManualOps]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent<CABIframeMessage>) => {
      const data = event.data;
      if (!data || typeof data !== 'object' || !data.type) return;

      switch (data.type) {
        case 'cab-save':
          if (data.ops && Array.isArray(data.ops)) {
            handleIframeSave(data.ops);
          }
          break;
        case 'cab-save-noop':
          // User clicked Save without making any changes — skip the API
          // call and just leave edit mode (Save button is intentionally
          // always-enabled to avoid the "why is this disabled?" UX).
          exitEditMode();
          break;
        case 'cab-review':
          if (Array.isArray(data.ops)) {
            handleReviewMessage(data.ops as PatchOp[]);
          }
          break;
        case 'cab-iframe-ready':
          if (isEditMode && iframeRef.current?.contentWindow) {
            iframeRef.current.contentWindow.postMessage({ type: 'cab-set-mode', editing: true }, '*');
            // If we're returning from review with manual ops to restore,
            // post them once the editor script is wired up.
            const opsToRestore = restoreOpsOnReadyRef.current;
            if (opsToRestore && opsToRestore.length > 0) {
              iframeRef.current.contentWindow.postMessage(
                { type: 'cab-restore-ops', ops: opsToRestore },
                '*',
              );
              restoreOpsOnReadyRef.current = null;
            }
          }
          // Editable / review iframe finished parsing — safe to drop the
          // entering-edit-mode overlay.
          setIsEnteringEditMode(false);
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleIframeSave, isEditMode, exitEditMode, handleReviewMessage]);

  // ── Diff jump navigation (Prev/Next buttons in AI Review toolbar) ──

  const jumpToChange = useCallback((kind: 'manual' | 'ai', direction: -1 | 1) => {
    if (!iframeRef.current?.contentWindow) return;
    const currentIdx = kind === 'manual' ? manualJumpIdx : aiJumpIdx;
    const nextIdx = currentIdx + direction;
    iframeRef.current.contentWindow.postMessage(
      { type: 'cab-jump', kind, index: nextIdx },
      '*',
    );
  }, [manualJumpIdx, aiJumpIdx]);

  // Listen for jump results from the iframe to update the index counter.
  useEffect(() => {
    const handleJumpResult = (event: MessageEvent) => {
      const data = event.data;
      if (!data || data.type !== 'cab-jump-result') return;
      if (data.kind === 'manual') setManualJumpIdx(data.index);
      else if (data.kind === 'ai') setAiJumpIdx(data.index);
    };
    window.addEventListener('message', handleJumpResult);
    return () => window.removeEventListener('message', handleJumpResult);
  }, []);

  // After a page-refresh hydration, jump to the first change that still needs
  // a decision (neither accepted nor rejected). Runs once both the diff index
  // (diffTotal > 0, set in buildDiffIndex) and the seeded opDecisions are
  // ready — whichever arrives last triggers the jump.
  useEffect(() => {
    if (!pendingFirstUndecidedJumpRef.current) return;
    if (diffTotal === 0) return; // index not built yet — wait for next render

    const entries = diffElementsRef.current;
    const firstUndecided = entries.findIndex(
      (e) => !opDecisions[`${e.kind}_${e.leafPath}`],
    );
    activateDiffAt(firstUndecided >= 0 ? firstUndecided : 0);
    pendingFirstUndecidedJumpRef.current = false;
  }, [diffTotal, opDecisions, activateDiffAt]);

  // Rebuild the diff index when review HTML loads (chat + validate paths).
  // The iframe onLoad handler (below) is the primary trigger since it fires
  // after the document is fully parsed. This effect serves as a fallback
  // for cases where onLoad already fired before this effect runs (e.g. when
  // reviewData changes without a full iframe reload).
  useEffect(() => {
    if (!isReviewMode || !reviewData?.diff_html) return;
    const iframe = iframeRef.current;
    if (!iframe) return;

    let cancelled = false;

    // Wait for the iframe document to be fully loaded before indexing.
    // For large documents the srcdoc parse can take longer than two rAFs.
    const tryIndex = () => {
      if (cancelled) return;
      const doc = iframe.contentDocument;
      if (!doc || doc.readyState === 'loading') {
        timerCleanupRef.current = setTimeout(tryIndex, 80);
        return;
      }
      const shouldJump = autoJumpOnReviewLoadRef.current;
      if (shouldJump) autoJumpOnReviewLoadRef.current = false;
      syncReviewDiffIndex({ autoJump: shouldJump });
    };

    // Use rAF to let React commit the srcDoc change first, then poll readyState.
    const frameId = requestAnimationFrame(() => {
      tryIndex();
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
      if (timerCleanupRef.current) {
        clearTimeout(timerCleanupRef.current);
        timerCleanupRef.current = null;
      }
    };
  }, [isReviewMode, reviewData?.review_id, reviewData?.diff_html, syncReviewDiffIndex]);

  // Click-to-select on diff spans in the review iframe.
  useEffect(() => {
    if (!isReviewMode) return;
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    const onClick = (e: MouseEvent) => {
      const target = (e.target as Element).closest('[data-cab-diff-target]');
      if (!target) return;
      e.stopPropagation();
      jumpToElement(target);
    };
    doc.addEventListener('click', onClick);
    return () => doc.removeEventListener('click', onClick);
  }, [isReviewMode, reviewData?.review_id, reviewData?.diff_html, jumpToElement]);

  // Keep iframe visual state in sync with persisted decisions after refresh.
  // Without this, opDecisions can be restored correctly while diff spans still
  // render with default <del>/<ins> styling (strikethrough visible).
  useEffect(() => {
    if (!isReviewMode) return;
    if (diffTotal === 0) return;
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;

    ensureDecisionCss(doc);
    for (const entry of diffElementsRef.current) {
      const key = `${entry.kind}_${entry.leafPath}`;
      const decision = opDecisions[key];
      entry.el.classList.remove('cab-diff-accepted', 'cab-diff-rejected');
      if (decision === 'accepted') entry.el.classList.add('cab-diff-accepted');
      else if (decision === 'rejected') entry.el.classList.add('cab-diff-rejected');
    }
  }, [isReviewMode, diffTotal, opDecisions, ensureDecisionCss]);

  // ────────────────────────────────────────────────────────────
  // CHAT HANDLERS
  // ────────────────────────────────────────────────────────────

  // Convert backend CabChatMessage to frontend Message format
  const convertChatMessage = useCallback((msg: CabChatMessage): Message => ({
    id: msg.message_id,
    role: msg.role,
    content: msg.content,
    timestamp: formatTimestamp(new Date(msg.timestamp * 1000), {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }),
    user_info: msg.user_info ? {
      id: msg.user_info.id,
      email: msg.user_info.email,
      username: msg.user_info.username,
      firstName: msg.user_info.firstName,
      lastName: msg.user_info.lastName,
    } : undefined,
    tokenUsage: msg.analytics ? {
      inputTokens: (msg.analytics.input_tokens || 0)
        + (msg.analytics.filter_cost?.input_tokens ?? 0),
      outputTokens: (msg.analytics.output_tokens || 0)
        + (msg.analytics.filter_cost?.output_tokens ?? 0),
      cacheReadInputTokens: (msg.analytics.cache_read_tokens || 0)
        + (msg.analytics.filter_cost?.cache_read_tokens ?? 0),
      cacheCreationInputTokens: (msg.analytics.cache_write_tokens || 0)
        + (msg.analytics.filter_cost?.cache_write_tokens ?? 0),
      costs: {
        input_cost: (msg.analytics.input_cost ?? 0)
          + (msg.analytics.filter_cost?.input_cost ?? 0),
        output_cost: (msg.analytics.output_cost ?? 0)
          + (msg.analytics.filter_cost?.output_cost ?? 0),
        cache_write_cost: (msg.analytics.cache_write_cost ?? 0)
          + (msg.analytics.filter_cost?.cache_write_cost ?? 0),
        cache_read_cost: (msg.analytics.cache_read_cost ?? 0)
          + (msg.analytics.filter_cost?.cache_read_cost ?? 0),
        total_cost: msg.analytics.total_cost_usd ?? msg.analytics.llm_cost_usd,
      },
    } : undefined,
    developmentTiming: (msg.analytics?.runtime_ms && msg.message_kind !== 'ask') ? {
      duration_seconds: msg.analytics.runtime_ms / 1000,
    } : undefined,
    model_preference: msg.model_preference ?? undefined,
  }), []);

  // Load chat messages from backend (initial load or refresh)
  const loadChatMessages = useCallback(async (preserveThinking = false) => {
    if (!projectId) return;
    try {
      const response = await getCabChatMessages(projectId);
      const messages = response.messages.map(convertChatMessage);
      if (preserveThinking) {
        // Preserve thinking message if one exists (e.g., from job recovery after page refresh)
        setChatMessages(prev => {
          const thinkingMsg = prev.find(msg => msg.isThinking);
          return thinkingMsg ? [...messages, thinkingMsg] : messages;
        });
      } else {
        setChatMessages(messages);
      }
      setHasMoreChatMessages(response.has_more);
      setChatMessagesLoaded(true);
    } catch (err) {
      console.error('Failed to load chat messages:', err);
    }
  }, [projectId, convertChatMessage]);

  // Load older chat messages (pagination)
  const loadMoreChatMessages = useCallback(async () => {
    if (!projectId || isLoadingMoreChatMessages || !hasMoreChatMessages) return;
    if (chatMessages.length === 0) return;

    const oldestMessage = chatMessages[0];
    if (!oldestMessage?.id) return;

    setIsLoadingMoreChatMessages(true);
    try {
      const response = await getCabChatMessages(projectId, oldestMessage.id, 10);
      const olderMessages = response.messages.map(convertChatMessage);
      // Prepend older messages to existing messages
      setChatMessages(prev => [...olderMessages, ...prev]);
      setHasMoreChatMessages(response.has_more);
    } catch (err) {
      console.error('Failed to load more chat messages:', err);
    } finally {
      setIsLoadingMoreChatMessages(false);
    }
  }, [projectId, chatMessages, isLoadingMoreChatMessages, hasMoreChatMessages, convertChatMessage]);

  // Load chat messages when CAB is completed and chat tab is active
  useEffect(() => {
    if (sessionStatus === 'completed' && !chatMessagesLoaded) {
      // Preserve thinking message if one exists (from job recovery after page refresh)
      loadChatMessages(true);
    }
  }, [sessionStatus, chatMessagesLoaded, loadChatMessages]);


  // Poll for chat job completion
  const pollChatJob = useCallback(async (jobId: string, pollIntervalSec: number) => {
    if (!projectId) return;

    const controller = new AbortController();
    chatPollAbortRef.current = controller;

    try {
      const result = await pollReviewJob(projectId, jobId, pollIntervalSec, {
        signal: controller.signal,
      });

      // Job completed - reload messages to get the assistant response (replaces thinking message)
      await loadChatMessages();

      // Refresh navbar cost/duration stats after chat response is received
      onProjectCostUpdate?.();

      // Only enter review mode if there are actual changes to review
      // When AI determines "no changes needed", ai_ops and manual_ops will be empty.
      // Read-only users cannot apply changes so skip review mode entirely for them.
      const hasChangesToReview = result &&
        (result.ai_ops.length > 0 || result.manual_ops.length > 0);
      const canApplyChanges = permissions.includes(Permissions.UpdateProjectPermission);

      if (hasChangesToReview && canApplyChanges) {
        setOpDecisions({});
        setCombinedIdx(-1);
        setActiveKind(null);
        setActiveEntry(null);
        setDiffTotal(0);
        diffElementsRef.current = [];
        autoJumpOnReviewLoadRef.current = true;
        setReviewData(result);
        setIsReviewMode(true);
        setIsEditMode(false);
        setEditableHtml(null);
      }
      // If no changes to review, or user is read-only, just keep chat enabled
    } catch (err: any) {
      if (err.name === 'AbortError') {
        console.log('Chat job polling cancelled');
        return;
      }
      console.error('Chat job failed:', err);
      // Remove the thinking bubble and inject a visible error message so the
      // user knows something went wrong rather than seeing the spinner vanish.
      const errorContent =
        err?.message && !err.message.toLowerCase().includes('failed')
          ? err.message
          : 'Something went wrong processing your request. Please try again.';
      setChatMessages(prev => {
        const withoutThinking = prev.filter((msg) => !msg.isThinking);
        const errorMsg: Message = {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: errorContent,
          timestamp: formatTimestamp(new Date(), { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }),
        };
        return [...withoutThinking, errorMsg];
      });
      // Reload to sync any partial server-side state
      await loadChatMessages();
    } finally {
      setIsSendingChatMessage(false);
      setActiveChatJobId(null);
      setIsValidating(false);
      setActiveReviewJobId(null); 
      chatPollAbortRef.current = null;
    }
  }, [projectId, loadChatMessages, loadPreview, onProjectCostUpdate]);

  const handleSendChatMessage = useCallback(async (content: string, tags: CABTag[], modelPreference?: string) => {
    if (!projectId || !content.trim()) return;

    try {
      setIsSendingChatMessage(true);

      // Get current CAB version for optimistic locking
      const versionResp = await getCABVersion(projectId);
      const baseVersion = versionResp.version;

      // Add optimistic user message to UI immediately
      const tempUserMessage: Message = {
        id: `temp-${Date.now()}`,
        role: 'user',
        content,
        timestamp: formatTimestamp(new Date(), { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }),
        user_info: currentUser ? {
          id: currentUser.id,
          email: currentUser.email,
          username: currentUser.username,
          firstName: currentUser.firstName,
          lastName: currentUser.lastName,
        } : undefined,
        tags: tags.map(tag => ({
          id: tag.id,
          label: tag.label,
          data: { jsonPath: tag.jsonPath },
        })),
      };
      setChatMessages(prev => [...prev, tempUserMessage]);

      // Add a "thinking" message placeholder
      const thinkingMessage: Message = {
        id: `thinking-${Date.now()}`,
        role: 'assistant',
        content: '',
        timestamp: formatTimestamp(new Date(), { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }),
        isThinking: true,
        thinkingMessage: 'Thinking...',
      };
      setChatMessages(prev => [...prev, thinkingMessage]);

      // Send message to backend
      const response = await sendCabChatMessage(projectId, {
        content,
        base_version: baseVersion,
        model_preference: modelPreference as 'medium' | 'high' | undefined,
      });

      // Update the user message with the real ID
      setChatMessages(prev =>
        prev.map(msg =>
          msg.id === tempUserMessage.id
            ? { ...msg, id: response.message_id }
            : msg
        )
      );

      // Start polling for job completion
      setActiveChatJobId(response.job_id);
      setIsValidating(true);
      setActiveReviewJobId(response.job_id);
      pollChatJob(response.job_id, response.poll_interval_sec);

    } catch (err: any) {
      console.error('Failed to send chat message:', err);
      setIsSendingChatMessage(false);

      // Remove the thinking message on error
      setChatMessages(prev => prev.filter(msg => !msg.isThinking));

      // Extract user-friendly error message from response
      const errorDetail = err.response?.data?.detail;
      let userMessage = 'Something went wrong. Please try again.';
      
      if (errorDetail) {
        userMessage = errorDetail;
      } else if (err.response?.status === 423) {
        userMessage = 'The project is currently locked. Another operation is in progress.';
      } else if (err.response?.status === 409) {
        userMessage = 'A conflict occurred. Please refresh and try again.';
      } else if (err.response?.status >= 500) {
        userMessage = 'Server error. Please try again later.';
      }

      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: userMessage,
        timestamp: formatTimestamp(new Date(), { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }),
      };
      setChatMessages(prev => [...prev, errorMessage]);
    }
  }, [projectId, currentUser, pollChatJob]);

  // Reset tab to config when CAB is deleted or new generation starts
  useEffect(() => {
    if (sessionStatus === 'idle' || sessionStatus === 'processing') {
      setActiveLeftTab('config');
      setChatMessages([]);
      setChatMessagesLoaded(false);
    }
  }, [sessionStatus]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (chatPollAbortRef.current) {
        chatPollAbortRef.current.abort();
      }
    };
  }, []);

  // ────────────────────────────────────────────────────────────
  // COMPUTED
  // ────────────────────────────────────────────────────────────

  const hasFiles = files.length > 0 || uploadingFiles.length > 0;
  const isProcessing = sessionStatus === 'processing';
  const hasGenerationStarted = sessionStatus === 'processing' || sessionStatus === 'paused' || sessionStatus === 'completed' || sessionStatus === 'error';
  const canGenerate = files.length > 0 && !isProcessing && !isUploading && enabledSections.length > 0;
  const showProgressSidebar = isProcessing || sessionStatus === 'paused' || sessionStatus === 'error';
  const isViewingSnapshot = selectedVersionTs != null;
  const canEdit = sessionStatus === 'completed' && !isProcessing && !isViewingSnapshot;
  // True once at least one change has been explicitly accepted or rejected.
  // Used to switch button labels from "Save All"/"Discard All" → "Save"/"Discard".
  const hasAnyDecision = Object.keys(opDecisions).length > 0;
  const showTabs = sessionStatus === 'completed' && !isViewingSnapshot;

  // Determine preview empty state variant
  // Keep showing 'no-files' until generation starts to avoid message flicker
  const previewVariant: 'no-files' | 'ready-to-generate' | 'processing' = isProcessing
    ? 'processing'
    : 'no-files';

  // Select which HTML to render in the iframe.
  // Priority: review diff > snapshot > edit > read-only preview.
  // While reviewing, the diff_html is a complete read-only doc with the
  // diff CSS baked in — no edit script runs. Snapshot view is disabled
  // during edit/review for the same reason.
  const displayHtml = useMemo(() => {
    if (isReviewMode && reviewData?.diff_html) return reviewData.diff_html;
    if (isViewingSnapshot) return versionHtml;
    if (isEditMode && editableHtml) return editableHtml;
    return previewHtml;
  }, [
    isReviewMode,
    reviewData,
    isViewingSnapshot,
    versionHtml,
    isEditMode,
    editableHtml,
    previewHtml,
  ]);

  // ────────────────────────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full w-full">
        <div className="w-8 h-8 border-2 border-border-secondary border-t-bg-brand-solid rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex h-full gap-bg-v2-xl overflow-hidden w-full">
      {/* ═══════ LEFT: Files + Controls + Progress Sidebar ═══════ */}
      <div className="flex-[4] min-w-0 flex gap-bg-v2-xl">
        {/* Main content panel */}
        <div className="flex-1 min-w-0 flex flex-col bg-bg-primary rounded-bg-v2-lg container-shadow-sm overflow-hidden">
          {showTabs ? (
            <>
              {/* Tabs - shown when CAB is completed */}
              <CABPanelTabs
                activeTab={activeLeftTab}
                onTabChange={setActiveLeftTab}
                disabled={false}
              />
              
              {/* Tab Content */}
              {activeLeftTab === 'config' ? (
                <>
                  {/* Config Tab Content */}
                  <div className="flex-1 overflow-y-auto p-bg-v2-xl">
                    <IfHasPermission
                      permissions={permissions}
                      requires={Permissions.UpdateProjectPermission}
                    >
                      <CABFileUpload
                        variant={hasFiles ? 'compact' : 'large'}
                        onFilesSelected={handleFilesSelected}
                        disabled={hasGenerationStarted}
                        isUploading={isUploading}
                      />
                    </IfHasPermission>
                    <CABFileList
                      files={files}
                      uploadingFiles={uploadingFiles}
                      onDeleteFile={handleDeleteFile}
                      onDownloadFile={handleDownloadFile}
                      disabled={hasGenerationStarted}
                      permissions={permissions}
                    />
                    {hasFiles && (
                      <CABSectionSelector
                        enabledSections={enabledSections}
                        onChange={setEnabledSections}
                        disabled={hasGenerationStarted}
                        readOnly={!permissions.includes(Permissions.UpdateProjectPermission)}
                      />
                    )}
                    {error && (
                      <div className="mt-bg-v2-md p-bg-v2-md bg-bg-error-primary rounded-bg-v2-md">
                        <Text variant="text-xs" className="text-text-error-primary">{error}</Text>
                      </div>
                    )}
                  </div>

                  {/* Cost panel */}
                  {costInfo && (
                    <CABCostPanel
                      cost={costInfo}
                      visible={sessionStatus === 'completed' || sessionStatus === 'error' || (costInfo.total_usd > 0)}
                      totalDurationSeconds={totalDurationSeconds}
                    />
                  )}

                  {/* Action bar */}
                  <CABActionBar
                    status={sessionStatus}
                    canGenerate={canGenerate}
                    hasFiles={hasFiles}
                    onGenerate={handleGenerate}
                    onPause={handlePause}
                    onStop={handleStop}
                    onDeleteAll={handleDeleteAll}
                    onResume={handleResume}
                    isUploading={isUploading}
                    permissions={permissions}
                    platformType={platformType}
                    onGenerateDesign={async () => {
                      if (!projectId || !onGenerateDesign) return;
                      onGenerateDesign();
                    }}
                  />
                </>
              ) : (
                <>
                  <CABChatPanel
                    projectId={projectId || ''}
                    messages={chatMessages}
                    onSendMessage={handleSendChatMessage}
                    isSending={isSendingChatMessage}
                    disabled={isReviewMode || isEditMode || isValidating}
                    readOnly={!permissions.includes(Permissions.UpdateProjectPermission)}
                    permissions={permissions}
                    currentUser={currentUser}
                    hasMoreMessages={hasMoreChatMessages}
                    isLoadingMore={isLoadingMoreChatMessages}
                    onLoadMore={loadMoreChatMessages}
                  />
                </>
              )}
            </>
          ) : (
            <>
              {/* Original Header - shown when CAB is not completed */}
              <div className="px-bg-v2-xl py-bg-v2-lg border-b border-border-secondary flex items-center min-h-[56px] shrink-0">
                <Text variant="text-md" weight="semibold" className="text-text-primary">
                  CAB Configuration
                </Text>
              </div>

              {/* Original Content */}
              <div className="flex-1 overflow-y-auto p-bg-v2-xl">
                <IfHasPermission
                  permissions={permissions}
                  requires={Permissions.UpdateProjectPermission}
                >
                  <CABFileUpload
                    variant={hasFiles ? 'compact' : 'large'}
                    onFilesSelected={handleFilesSelected}
                    disabled={hasGenerationStarted}
                    isUploading={isUploading}
                  />
                </IfHasPermission>
                <CABFileList
                  files={files}
                  uploadingFiles={uploadingFiles}
                  onDeleteFile={handleDeleteFile}
                  onDownloadFile={handleDownloadFile}
                  disabled={hasGenerationStarted}
                  permissions={permissions}
                />
                {hasFiles && (
                  <CABSectionSelector
                    enabledSections={enabledSections}
                    onChange={setEnabledSections}
                    disabled={hasGenerationStarted}
                    readOnly={!permissions.includes(Permissions.UpdateProjectPermission)}
                  />
                )}
                {error && (
                  <div className="mt-bg-v2-md p-bg-v2-md bg-bg-error-primary rounded-bg-v2-md">
                    <Text variant="text-xs" className="text-text-error-primary">{error}</Text>
                  </div>
                )}
              </div>

              {/* Cost panel */}
              {costInfo && (
                <CABCostPanel
                  cost={costInfo}
                  visible={sessionStatus === 'completed' || sessionStatus === 'error' || (costInfo.total_usd > 0)}
                  totalDurationSeconds={totalDurationSeconds}
                />
              )}

              {/* Action bar */}
              <CABActionBar
                status={sessionStatus}
                canGenerate={canGenerate}
                hasFiles={hasFiles}
                onGenerate={handleGenerate}
                onPause={handlePause}
                onStop={handleStop}
                onDeleteAll={handleDeleteAll}
                onResume={handleResume}
                isUploading={isUploading}
                permissions={permissions}
                platformType={platformType}
                onGenerateDesign={async () => {
                  if (!projectId || !onGenerateDesign) return;
                  onGenerateDesign();
                }}
              />
            </>
          )}
        </div>

        {/* Progress sidebar */}
        <CABProgressSidebar
          phases={phases}
          visible={showProgressSidebar}
          sessionStatus={sessionStatus}
        />
      </div>

      {/* ═══════ RIGHT: Preview Panel ═══════ */}
      <div className="flex-[6] min-w-0 flex flex-col bg-bg-primary rounded-bg-v2-lg container-shadow-sm overflow-hidden">
        {/* Preview header — title row + (review-mode only) sub-bar */}
        <div className="border-b border-border-secondary shrink-0">
          <div className="px-bg-v2-xl py-bg-v2-lg flex justify-between items-center min-h-[56px] gap-bg-v2-md">
          <div className="flex items-center gap-bg-v2-sm min-w-0">
            <Text variant="text-md" weight="semibold" className="text-text-primary">
              Common Application Blueprint Preview
            </Text>
            {isEditMode && (
              <span className="inline-flex items-center gap-bg-v2-xs bg-utility-purple-100 text-utility-purple-700 px-bg-v2-sm py-bg-v2-xxs rounded-bg-v2-sm text-bg-v2-xs font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-utility-purple-700"></span>
                Editing
              </span>
            )}
            {isViewingSnapshot && !isEditMode && !isReviewMode && (
              <span className="inline-flex items-center gap-bg-v2-xs bg-utility-blue-100 text-utility-blue-700 px-bg-v2-sm py-bg-v2-xxs rounded-bg-v2-sm text-bg-v2-xs font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-utility-blue-700"></span>
                Viewing previous version
              </span>
            )}
          </div>
          <div className="flex gap-bg-v2-sm">
            {isReviewMode ? (
              // Review toolbar: Save / Save All respects per-change decisions (pending = accepted);
              // Discard / Discard All reverts everything regardless of individual decisions.
              // Labels switch from "…All" to plain once the user makes at least one decision.
              // Read-only users only see Discard (to exit review mode cleanly).
              <React.Fragment key="cab-toolbar-review">
                {permissions.includes(Permissions.UpdateProjectPermission) && (
                  <Button
                    type="button"
                    label={isApplyingReview ? 'Saving...' : (hasAnyDecision ? 'Save' : 'Save All')}
                    onClick={handleSaveAll}
                    variant="primary"
                    size="small"
                    loading={isApplyingReview}
                    disabled={isApplyingReview}
                    title={hasAnyDecision ? 'Save with your current decisions applied' : 'Save all changes'}
                  />
                )}
                <Button
                  type="button"
                  label={hasAnyDecision ? 'Discard' : 'Discard All'}
                  onClick={() => handleApplyReview('discard')}
                  variant="outline"
                  size="small"
                  disabled={isApplyingReview}
                  title="Discard all manual edits and AI suggestions"
                />
              </React.Fragment>
            ) : isEditMode ? (
              // Keyed fragment so React unmounts these buttons (and their
              // tooltips) when leaving edit mode, instead of reconciling
              // them with the read-only buttons in the same slot — which
              // would otherwise carry over Tooltip state (isVisible/timers)
              // onto an unrelated button (e.g. Save → Open in New Tab).
              <React.Fragment key="cab-toolbar-edit">
                <Button
                  type="button"
                  label={isValidating ? 'Analyzing Change Impact...' : 'Review with AI'}
                  onClick={() => {
                    iframeRef.current?.contentWindow?.postMessage({ type: 'cab-request-review' }, '*');
                  }}
                  variant="outline"
                  size="small"
                  loading={isValidating}
                  title="Analyze Change Impact with AI"
                  disabled={isSaving || isValidating}
                />
                <Button
                  type="button"
                  label={isSaving ? 'Saving...' : 'Save Changes'}
                  onClick={() => {
                    iframeRef.current?.contentWindow?.postMessage({ type: 'cab-request-save' }, '*');
                  }}
                  variant="primary"
                  size="small"
                  loading={isSaving}
                  title='Save Changes'
                  disabled={isSaving || isValidating}
                />
                <Button
                  type="button"
                  label={<Icon name={IconName.Close} size="sm" inheritColor />}
                  onClick={() => {
                    iframeRef.current?.contentWindow?.postMessage({ type: 'cab-discard' }, '*');
                    exitEditMode();
                  }}
                  variant="outline"
                  size="small"
                  title='Cancel'
                  disabled={isSaving || isValidating}
                />
              </React.Fragment>
            ) : (
              <React.Fragment key="cab-toolbar-view">
                {sessionStatus === 'completed' && !isViewingSnapshot && (platformType || enableTicketGeneration) && (
                  <IfHasPermission permissions={permissions} requires={Permissions.UpdateProjectPermission}>
                    <button
                      type="button"
                      disabled={isGeneratingTickets}
                      onClick={handleGenerateTicketsClick}
                      className="
                        px-bg-v2-lg py-bg-v2-sm border border-transparent rounded-bg-v2-md
                        bg-bg-brand-solid text-text-primary-on-brand text-bg-v2-xs font-bg-v2-medium
                        flex items-center gap-bg-v2-xs transition-colors
                        hover:bg-bg-brand-solid-hover whitespace-nowrap
                        disabled:opacity-40 disabled:cursor-not-allowed
                      "
                    >
                      {isGeneratingTickets
                        ? <Icon name={IconName.Loading} size="sm" inheritColor />
                        : <Icon name={IconName.Tickets} size="sm" inheritColor />}
                      {isGeneratingTickets ? 'Generating...' : 'Generate Tickets'}
                    </button>
                  </IfHasPermission>
                )}
                {sessionStatus === 'completed' && versions.length > 0 && (
                  <CABVersionHistory
                    versions={versions}
                    selectedTimestampMs={selectedVersionTs}
                    onSelectVersion={setSelectedVersionTs}
                    onClearSelection={() => setSelectedVersionTs(null)}
                    onRestoreVersion={(ts) => setRestoreTarget(ts)}
                    isRestoring={isRestoring}
                    permissions={permissions}
                  />
                )}
                <Button
                  type="button"
                  label={<Icon name={IconName.ExternalLink} size="sm" inheritColor />}
                  onClick={() => {
                    if (!projectId) return;
                    window.open(`/project/${projectId}/cab${cabSessionId ? `/${cabSessionId}` : ''}`, '_blank');
                  }}
                  variant="outline"
                  size="very-small"
                  className="!w-8 !h-8 !p-0 !min-w-0"
                  disabled={sessionStatus !== 'completed' || isViewingSnapshot}
                  title="Open in New Tab"
                  tooltipPosition="bottom"
                />
                <IfHasPermission permissions={permissions} requires={Permissions.UpdateProjectPermission}>
                  <Button
                    type="button"
                    label={<Icon name={IconName.Share} size="sm" inheritColor />}
                    onClick={() => setShowShareModal(true)}
                    variant="outline"
                    size="very-small"
                    className="!w-8 !h-8 !p-0 !min-w-0"
                    disabled={sessionStatus !== 'completed' || isViewingSnapshot}
                    title="Share CAB"
                    tooltipPosition="bottom"
                  />
                </IfHasPermission>
                <IfHasPermission permissions={permissions} requires={Permissions.UpdateProjectPermission}>
                  <Button
                    type="button"
                    label={isLoadingEditable ? null : <Icon name={IconName.Edit} size="sm" inheritColor />}
                    onClick={enterEditMode}
                    variant="outline"
                    size="very-small"
                    className="!w-8 !h-8 !p-0 !min-w-0"
                    disabled={!canEdit || isLoadingEditable}
                    loading={isLoadingEditable}
                    title="Edit CAB"
                    tooltipPosition="bottom"
                  />
                </IfHasPermission>
                <IfHasPermission permissions={permissions} requires={Permissions.DownloadPermission}>
                  <Button
                    type="button"
                    label={isDownloadingMarkdown ? null : <Icon name={IconName.FileDownload} size="sm" inheritColor />}
                    onClick={async () => {
                      if (!projectId || isDownloadingMarkdown) return;
                      setIsDownloadingMarkdown(true);
                      try {
                        await downloadCABMarkdown(projectId);
                      } catch (err) {
                        console.error('Failed to download CAB Markdown:', err);
                      } finally {
                        setIsDownloadingMarkdown(false);
                      }
                    }}
                    variant="outline"
                    size="very-small"
                    className="!w-8 !h-8 !p-0 !min-w-0"
                    disabled={sessionStatus !== 'completed' || isViewingSnapshot}
                    loading={isDownloadingMarkdown}
                    title={isDownloadingMarkdown ? 'Downloading…' : 'Download Markdown'}
                    tooltipPosition="bottom"
                  />
                  <Button
                    type="button"
                    label={isDownloadingPdf ? null : <Icon name={IconName.PdfDownload} size="sm" inheritColor />}
                    onClick={async () => {
                      if (!projectId || isDownloadingPdf) return;
                      setIsDownloadingPdf(true);
                      try {
                        await downloadCABPdf(projectId);
                      } catch (err) {
                        console.error('Failed to download CAB PDF:', err);
                      } finally {
                        setIsDownloadingPdf(false);
                      }
                    }}
                    variant="outline"
                    size="very-small"
                    className="!w-8 !h-8 !p-0 !min-w-0"
                    disabled={sessionStatus !== 'completed' || isViewingSnapshot}
                    loading={isDownloadingPdf}
                    title={isDownloadingPdf ? 'Generating PDF…' : 'Download PDF'}
                    tooltipPosition="bottom"
                  />
                </IfHasPermission>
              </React.Fragment>
            )}
          </div>
          </div>
          {/* Sub-bar — appears only in AI Review mode, directly under the title row */}
          {isReviewMode && (
            <div className="px-bg-v2-xl py-bg-v2-sm flex items-center justify-between gap-bg-v2-xs flex-wrap border-t border-border-secondary bg-bg-secondary">
              {/* Left: AI Review badge */}
              <span
                className="inline-flex items-center gap-bg-v2-xs bg-utility-orange-100 text-utility-orange-700 px-bg-v2-sm py-bg-v2-xxs rounded-bg-v2-sm text-bg-v2-xs font-semibold shrink-0"
                title="Review mode — accept or discard each change, then Save All"
              >
                <SparkleIcon className="w-3.5 h-3.5" />
                AI Review
              </span>

              {reviewData && (() => {
                const manualTotal = reviewData.manual_ops.length;
                // Use leaf-aware diffTotal so this chip matches the "X / N" navigation counter.
                // diffTotal covers all leaves (manual + AI); subtract manual op count as proxy
                // for manual leaves (1:1 ratio) to isolate the AI leaf count.
                // Falls back to raw op count before the diff index is built (diffTotal === 0).
                const aiTotal = diffTotal > 0
                  ? diffTotal - manualTotal
                  : reviewData.ai_ops.length;
                const manualAccepted = reviewData.manual_ops.filter(op => opDecisions[`manual_${op.path}`] === 'accepted').length;
                const manualRejected = reviewData.manual_ops.filter(op => opDecisions[`manual_${op.path}`] === 'rejected').length;
                const aiAccepted = reviewData.ai_ops.filter(op => opDecisions[`ai_${op.path}`] === 'accepted').length;
                const aiRejected = reviewData.ai_ops.filter(op => opDecisions[`ai_${op.path}`] === 'rejected').length;

                return (
                  <div className="flex items-center gap-bg-v2-xs flex-wrap">
                    {/* Manual edits count chip — display only */}
                    {manualTotal > 0 && (
                      <span className="inline-flex items-center gap-bg-v2-xs bg-utility-blue-50 text-utility-blue-700 border border-utility-blue-200 px-bg-v2-sm py-bg-v2-xxs rounded-bg-v2-sm text-bg-v2-xs font-medium">
                        <Icon name={IconName.Edit} size="xs" inheritColor />
                        <span className="tabular-nums font-semibold">{manualTotal}</span>
                        <span className="opacity-80">{manualTotal === 1 ? 'your edit' : 'your edits'}</span>
                      </span>
                    )}

                    {/* AI suggestions count chip — display only */}
                    {aiTotal > 0 && (
                      <span className="inline-flex items-center gap-bg-v2-xs bg-utility-purple-50 text-utility-purple-700 border border-utility-purple-200 px-bg-v2-sm py-bg-v2-xxs rounded-bg-v2-sm text-bg-v2-xs font-medium">
                        <SparkleIcon className="w-3.5 h-3.5" />
                        <span className="tabular-nums font-semibold">{aiTotal}</span>
                        <span className="opacity-80">{aiTotal === 1 ? 'AI suggestion' : 'AI suggestions'}</span>
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* ── Change detail bar ────────────────────────────────────────────
              Appears after the user first navigates to a change via Prev/Next.
              Left-border accent colours by kind; compact two-row layout.      */}
          {isReviewMode && reviewData && (() => {
            // grandTotal = number of navigable diff entries in the DOM (leaf-aware).
            // Falls back to op-count if the index hasn't been built yet.
            const opTotal = reviewData.manual_ops.length + reviewData.ai_ops.length;
            const grandTotal = diffTotal > 0 ? diffTotal : opTotal;
            const navDisabled = diffTotal === 0;
            if (grandTotal === 0) return null;

            // Decision tally counts each leaf entry — matches the "X / N" counter exactly.
            let totalAccepted = 0;
            let totalRejected = 0;
            if (diffElementsRef.current.length > 0) {
              for (const entry of diffElementsRef.current) {
                const d = opDecisions[`${entry.kind}_${entry.leafPath}`];
                if (d === 'accepted') totalAccepted++;
                else if (d === 'rejected') totalRejected++;
              }
            } else {
              totalAccepted = reviewData.manual_ops.filter(o => opDecisions[`manual_${o.path}`] === 'accepted').length
                            + reviewData.ai_ops.filter(o => opDecisions[`ai_${o.path}`] === 'accepted').length;
              totalRejected = reviewData.manual_ops.filter(o => opDecisions[`manual_${o.path}`] === 'rejected').length
                            + reviewData.ai_ops.filter(o => opDecisions[`ai_${o.path}`] === 'rejected').length;
            }
            const totalPending  = grandTotal - totalAccepted - totalRejected;

            // Decision tally — shown in both the "nothing focused" state and the active state
            const decisionTally = (totalAccepted > 0 || totalRejected > 0) && (
              <span className="inline-flex items-center gap-1 shrink-0">
                {totalAccepted > 0 && (
                  <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold tabular-nums text-utility-success-600">
                    <span>✓</span><span>{totalAccepted}</span>
                  </span>
                )}
                {totalRejected > 0 && (
                  <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold tabular-nums text-utility-error-600">
                    <span>✗</span><span>{totalRejected}</span>
                  </span>
                )}
                {totalPending > 0 && (
                  <span className="inline-flex items-center gap-0.5 text-[11px] tabular-nums text-text-quaternary">
                    <span>○</span><span>{totalPending}</span>
                  </span>
                )}
              </span>
            );

            // If nothing focused yet, show a prompt to use the nav buttons
            if (!activeKind || combinedIdx < 0) {
              return (
                <div className={`border-t bg-bg-secondary px-bg-v2-xl py-bg-v2-sm flex items-center justify-between gap-bg-v2-md border-t-border-secondary`}>
                  <span className="text-xs text-text-quaternary italic">Use ‹ › to navigate changes</span>
                  <div className="flex items-center gap-1 shrink-0">
                    <button type="button" onClick={() => jumpToAny(-1)}
                      disabled={navDisabled}
                      className="inline-flex items-center justify-center w-7 h-7 rounded border border-border-secondary bg-bg-primary hover:bg-bg-secondary text-text-secondary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Previous change" aria-label="Previous change">
                      <Icon name={IconName.ChevronRight} size="sm" inheritColor className="rotate-180" />
                    </button>
                    <span className="text-xs tabular-nums text-text-quaternary px-1">— / {grandTotal}</span>
                    <button type="button" onClick={() => jumpToAny(1)}
                      disabled={navDisabled}
                      className="inline-flex items-center justify-center w-7 h-7 rounded border border-border-secondary bg-bg-primary hover:bg-bg-secondary text-text-secondary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Next change" aria-label="Next change">
                      <Icon name={IconName.ChevronRight} size="sm" inheritColor />
                    </button>
                  </div>
                </div>
              );
            }

            // Use the live activeEntry (leaf-level data from the DOM) so the detail bar
            // describes EXACTLY the highlighted diff span — not the parent op's blob.
            if (!activeEntry) return null;
            const isManual   = isManualDiffKind(activeEntry.kind);
            const isInsert   = activeEntry.kind === 'ai-insert';
            const isRemove   = activeEntry.kind === 'ai-remove';
            const key        = `${activeEntry.kind}_${activeEntry.leafPath}`;
            const decision   = opDecisions[key];

            // Border colour: blue=manual, green=insert, red=remove, purple=ai set
            const borderCls = isManual
              ? 'border-t-utility-blue-100 border-l-utility-blue-400'
              : isInsert
                ? 'border-t-utility-success-100 border-l-utility-success-500'
                : isRemove
                  ? 'border-t-utility-error-100 border-l-utility-error-500'
                  : 'border-t-utility-purple-100 border-l-utility-purple-400';

            // Label colour
            const labelCls = isManual
              ? 'text-utility-blue-600'
              : isInsert
                ? 'text-utility-success-600'
                : isRemove
                  ? 'text-utility-error-600'
                  : 'text-utility-purple-600';

            // Human-readable label
            const kindLabel = isManual
              ? '✎ Your edit'
              : isInsert
                ? 'AI Added'
                : isRemove
                  ? 'AI Deleted'
                  : '✦ AI suggested';

            return (
              <div className={`border-t border-l-[3px] bg-bg-secondary px-bg-v2-xl py-bg-v2-sm ${borderCls}`}>

                {/* Row 1 — kind · breadcrumb · tally · combined progress + prev/next */}
                <div className="flex items-center justify-between gap-bg-v2-md mb-1.5">
                  <div className="flex items-center gap-bg-v2-xs min-w-0 overflow-hidden">
                    <span className={`text-xs font-semibold uppercase tracking-wide shrink-0 ${labelCls}`}>
                      {kindLabel}
                    </span>
                    <span className="text-text-quaternary text-xs shrink-0">·</span>
                    <span className="text-xs text-text-tertiary truncate" title={activeEntry.leafPath}>
                      {pathToBreadcrumb(activeEntry.leafPath)}
                    </span>
                  </div>

                  {/* Combined navigation */}
                  <div className="flex items-center gap-1 shrink-0">
                    {decisionTally}
                    {decisionTally && <span className="w-px h-3.5 bg-border-secondary mx-1 shrink-0" />}
                    <button type="button" onClick={() => jumpToAny(-1)}
                      className="inline-flex items-center justify-center w-6 h-6 rounded border border-border-secondary bg-bg-primary hover:bg-bg-secondary text-text-secondary transition-colors"
                      title="Previous change" aria-label="Previous change">
                      <Icon name={IconName.ChevronRight} size="xs" inheritColor className="rotate-180" />
                    </button>
                    <span className="text-xs tabular-nums text-text-secondary font-medium px-0.5">
                      {combinedIdx + 1} / {grandTotal}
                    </span>
                    <button type="button" onClick={() => jumpToAny(1)}
                      className="inline-flex items-center justify-center w-6 h-6 rounded border border-border-secondary bg-bg-primary hover:bg-bg-secondary text-text-secondary transition-colors"
                      title="Next change" aria-label="Next change">
                      <Icon name={IconName.ChevronRight} size="xs" inheritColor />
                    </button>
                  </div>
                </div>

                {/* Row 2 — WAS / NOW + Accept/Discard buttons */}
                <div className="flex items-center gap-bg-v2-md">
                  <div className="flex-1 flex flex-col gap-1 min-w-0">
                    {/* Insert: only NOW; Remove: only WAS; Set: both */}
                    {!isInsert && (
                      <div className="flex items-baseline gap-2 min-w-0">
                        <span className="text-[10px] font-bold text-text-quaternary uppercase tracking-[0.08em] shrink-0">
                          {isRemove ? 'REMOVING' : 'WAS'}
                        </span>
                        <span className={`text-xs leading-snug truncate ${isRemove ? 'text-text-tertiary line-through' : 'text-text-tertiary'}`}>
                          {truncateValue(activeEntry.oldValue) || '—'}
                        </span>
                      </div>
                    )}
                    {!isRemove && (
                      <div className="flex items-baseline gap-2 min-w-0">
                        <span className="text-[10px] font-bold text-utility-success-600 uppercase tracking-[0.08em] shrink-0">
                          {isInsert ? 'ADDING' : 'NOW'}
                        </span>
                        <span className="text-xs text-text-primary font-medium leading-snug truncate">
                          {truncateValue(activeEntry.newValue) || '—'}
                        </span>
                      </div>
                    )}
                    {!isManual && activeEntry.rationale && (
                      <p className="text-[11px] text-text-quaternary italic leading-snug truncate pl-[28px]">
                        {activeEntry.rationale}
                      </p>
                    )}
                  </div>

                  <div className="flex gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => handleDecide(activeEntry, 'accepted')}
                      title={decision === 'accepted' ? 'Click to undo' : 'Accept this change'}
                      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs font-semibold border transition-colors ${
                        decision === 'accepted'
                          ? 'bg-utility-success-100 text-utility-success-700 border-utility-success-200'
                          : 'bg-bg-primary text-utility-success-600 border-utility-success-200 hover:bg-utility-success-50'
                      }`}
                    >
                      ✓ Accept
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDecide(activeEntry, 'rejected')}
                      title={decision === 'rejected' ? 'Click to undo' : 'Discard this change'}
                      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs font-semibold border transition-colors ${
                        decision === 'rejected'
                          ? 'bg-utility-error-100 text-utility-error-700 border-utility-error-200'
                          : 'bg-bg-primary text-utility-error-600 border-utility-error-200 hover:bg-utility-error-50'
                      }`}
                    >
                      ✗ Discard
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Preview content */}
        <div className="flex-1 overflow-hidden relative">
          {displayHtml ? (
            <iframe
              ref={iframeRef}
              srcDoc={displayHtml}
              className="w-full h-full border-0"
              title={isEditMode ? 'CAB Edit' : 'CAB Preview'}
              sandbox="allow-scripts allow-same-origin"
              onLoad={() => {
                // Read-only iframe finished re-parsing after we left edit mode.
                // (For the editable iframe, `cab-iframe-ready` is what we wait
                // on — the script runs after this, so onLoad is too early.)
                if (isExitingEditMode) {
                  setIsExitingEditMode(false);
                  if (exitTimeoutRef.current) {
                    window.clearTimeout(exitTimeoutRef.current);
                    exitTimeoutRef.current = null;
                  }
                }
                // Rebuild diff index once the iframe document is fully parsed.
                // This is the most reliable trigger for large documents where
                // the rAF-based useEffect might fire before parsing completes.
                if (isReviewMode && reviewData?.diff_html) {
                  const shouldJump = autoJumpOnReviewLoadRef.current;
                  if (shouldJump) autoJumpOnReviewLoadRef.current = false;
                  syncReviewDiffIndex({ autoJump: shouldJump });
                }
              }}
            />
          ) : isLoadingPreview || isLoadingEditable || isLoadingVersionHtml ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-8 h-8 border-2 border-border-secondary border-t-bg-brand-solid rounded-full animate-spin" />
            </div>
          ) : sessionStatus === 'error' && errorInfo ? (
            <CABErrorState
              error={errorInfo}
              onResume={handleResume}
              onRegenerate={handleRegenerate}
            />
          ) : (
            <CABEmptyState
              variant={previewVariant}
              fileCount={files.length}
            />
          )}

          {/* Loading overlay layered over the preview.
              Shown during: entering edit mode, leaving edit mode, save in
              progress, fetching a snapshot version, or refreshing the
              latest CAB after a save. Sits inside the preview container
              (absolute) so it covers only the iframe area. */}
          {(displayHtml || isEnteringEditMode) && (
            isSaving ||
            isLoadingVersionHtml ||
            isEnteringEditMode ||
            isExitingEditMode ||
            isValidating ||
            isApplyingReview ||
            (isLoadingPreview && !isEditMode && !isReviewMode)
          ) && (
            <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/20">
              <div className="bg-bg-primary rounded-bg-v2-md px-bg-v2-lg py-bg-v2-md container-shadow-md flex items-center gap-bg-v2-sm">
                <Icon name={IconName.Loading} size="sm" spin inheritColor />
                <Text variant="text-sm" color="secondary">
                  {isValidating
                    ? 'Analyzing with AI...'
                    : isApplyingReview
                      ? 'Applying review...'
                      : isSaving
                        ? (isLoadingPreview ? 'Loading latest version...' : 'Saving changes...')
                        : isLoadingVersionHtml
                          ? 'Loading version preview...'
                          : isEnteringEditMode
                            ? (isLoadingEditable ? 'Loading editor...' : 'Preparing editor...')
                            : isExitingEditMode
                              ? 'Preparing viewer...'
                              : 'Loading latest version...'}
                </Text>
              </div>
            </div>
          )}

          {/* Ticket generation progress overlay */}
          {(isGeneratingTickets || ticketGenDone) && !isEditMode && (
            <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/30">
              <div className="bg-bg-primary rounded-bg-v2-lg px-bg-v2-2xl py-bg-v2-xl container-shadow-lg flex flex-col items-center gap-bg-v2-md w-[360px]">
                {ticketGenDone ? (
                  <>
                    {ticketGenError ? (
                      <div className="w-10 h-10 rounded-full bg-utility-error-100 flex items-center justify-center">
                        <Icon name={IconName.Alert} size="md" className="!text-utility-error-600" inheritColor />
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-utility-success-100 flex items-center justify-center">
                        <Icon name={IconName.CheckCircle} size="md" className="!text-utility-success-600" inheritColor />
                      </div>
                    )}
                    <Text variant="text-md" weight="semibold" color="primary">
                      {ticketGenError
                        ? 'Ticket Generation Failed'
                        : ticketGenAlreadyRunning
                          ? 'Ticket Generation Already In Progress'
                          : 'Ticket Generation Started'}
                    </Text>
                    <Text variant="text-sm" color="secondary" className="text-center">
                      {ticketGenMessage}
                    </Text>
                    <div className="flex items-center gap-bg-v2-sm w-full pt-bg-v2-xs">
                      {!ticketGenError && onNavigateToTickets && (
                        <button
                          type="button"
                          onClick={() => { setTicketGenDone(false); setTicketGenError(false); setTicketGenAlreadyRunning(false); onNavigateToTickets(); }}
                          className="flex-1 bg-bg-brand-solid text-text-primary-on-brand px-bg-v2-lg py-bg-v2-md rounded-bg-v2-md font-bg-v2-semibold text-bg-v2-sm transition-colors hover:bg-bg-brand-solid-hover"
                        >
                          View Tickets
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => { setTicketGenDone(false); setTicketGenError(false); setTicketGenAlreadyRunning(false); }}
                        className="flex-1 border border-border-primary bg-bg-primary text-text-secondary px-bg-v2-lg py-bg-v2-md rounded-bg-v2-md text-bg-v2-sm font-bg-v2-medium transition-colors hover:bg-bg-primary-hover"
                      >
                        Dismiss
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-10 h-10 rounded-full bg-bg-secondary flex items-center justify-center">
                      <Icon name={IconName.Tickets} size="md" className="!text-text-primary" inheritColor />
                    </div>
                    <Text variant="text-md" weight="semibold" color="primary">Starting Ticket Generation</Text>
                    <Text variant="text-sm" color="secondary" className="text-center">
                      Sending CAB to AI Model for ticket generation...
                    </Text>
                    <div className="w-8 h-8 border-2 border-border-secondary border-t-bg-brand-solid rounded-full animate-spin mt-bg-v2-xs" />
                  </>
                )}
              </div>
            </div>
          )}

          {editError && (
            <div className="absolute top-2 left-2 bg-red-100 text-red-800 px-3 py-1 rounded text-xs font-medium shadow-sm">
              {editError}
            </div>
          )}
          {/* Toast notification for save success/error */}
          {toastMessage && (
            <div className="absolute bottom-4 left-4 z-50 max-w-sm shadow-lg">
              <FormAlert
                message={toastMessage.message}
                variant={toastMessage.variant}
                autoDismiss
                autoDismissTimeout={4000}
                onDismiss={() => setToastMessage(null)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <CABShareModal
          projectId={projectId!}
          cabSessionId={cabSessionId}
          shareableLinkEnabled={shareableLinkEnabled}
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          onSave={async (enabled: boolean) => {
            const result = await updateCABShareSettings(projectId!, enabled);
            setShareableLinkEnabled(result.readonly_sharable_link_enabled);
          }}
        />
      )}

      {/* Restore Version Confirmation Modal */}
      <Modal
        isOpen={restoreTarget != null}
        onClose={() => { if (!isRestoring) setRestoreTarget(null); }}
        title="Restore this version?"
        size="sm"
        onSubmit={handleConfirmRestore}
        onCancel={() => { if (!isRestoring) setRestoreTarget(null); }}
        submitLabel={isRestoring ? 'Restoring...' : 'Restore'}
        cancelLabel="Cancel"
        submitLoading={isRestoring}
      >
        <Text color="secondary">
          {restoreTarget != null
            ? `This will replace the current Common Application Blueprint with the version from ${new Date(restoreTarget).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}. All versions created after this one will be permanently deleted. This action cannot be undone.`
            : ''}
        </Text>
      </Modal>

      {/* Ticket Generation Confirmation Modal */}
      <Modal
        isOpen={showTicketGenConfirm}
        onClose={() => setShowTicketGenConfirm(false)}
        title="Regenerate Tickets?"
        size="sm"
        onSubmit={() => {
          setShowTicketGenConfirm(false);
          handleGenerateTickets();
        }}
        onCancel={() => setShowTicketGenConfirm(false)}
        submitLabel="Regenerate"
        cancelLabel="Cancel"
      >
        <Text color="secondary">
          {enableTicketGeneration
            ? 'Tickets already exist from this Blueprint. Regenerating will replace all previously generated CAB tickets. Are you sure?'
            : 'Tickets already exist from this Blueprint. Regenerating will replace all previously generated CAB tickets when the AI Model completes. Are you sure?'}
        </Text>
      </Modal>

    </div>
  );
};

export default CABPage;
